/**
 * Human-readable labels for every signal in the signal → axis registry. The
 * config UI uses these — the matrix never shows raw keys like
 * `pm_llm_has_owned_function_at_scale`. Keys are still the source of truth
 * in code; labels are pure display.
 */

export type SignalMeasurement = "keyword" | "numeric" | "llm";

/**
 * Lifecycle/availability of a signal in the management layer. Signals are NEVER
 * deleted — extraction and persistence are untouched regardless of status, so
 * historical evaluations stay reproducible and a status flip is instantly
 * reversible. Status only governs whether a signal is OFFERED for selection:
 *
 *   - `active`        — offered everywhere: the bootstrap candidate pool, the
 *                       routing-matrix UI, and the GEPA search space.
 *   - `experimental`  — NOT auto-offered during bootstrap (so a new extractor
 *                       being trialed doesn't pollute every freshly bootstrapped
 *                       model), but manually routable in the matrix UI and
 *                       eligible for GEPA optimization.
 *   - `deprecated`    — not offered for NEW selection anywhere and excluded from
 *                       the GEPA search space; still computed + persisted, and
 *                       still rendered (flagged) where a model already routes it,
 *                       so existing models score byte-identically until an author
 *                       drops the edge.
 *
 * This is the "manage, don't delete" layer: collapsing a redundant cluster means
 * marking the loser `deprecated` (reversibly), not removing its extractor.
 */
export type SignalStatus = "active" | "experimental" | "deprecated";

export type SignalGroup =
  | "founder"
  | "progression"
  | "management"
  | "stakeholder"
  | "product-systems"
  | "business"
  | "hr"
  | "environment"
  | "academic"
  | "priors"
  | "impact"
  | "recognition"
  | "credentials"
  | "geography"
  | "flags"
  | "ic-depth"
  | "autonomy-depth"
  | "engineering";

/**
 * Scoring-grade interpretability for one signal — written for the LLM JUDGE,
 * not for display (2026-07 catalog curation). Consumed by the runtime
 * glossary (`renderUncoveredSignalGlossary`) and piped into the axis-prompt
 * generators, so a routed signal always travels with real evidence semantics.
 *
 * Authoring rules (grounded in each signal's EXTRACTOR definition — the
 * firing conditions are the truth):
 *  - LEVEL-FREE: describe what the evidence establishes in seniority-semantic
 *    terms; mapping to rungs is the axis prompt's job (models have different
 *    ranges and the same signal can be strong on one axis, weak on another).
 *  - `licenses` speaks only about PRESENT values. Never describe `false`/`0`
 *    as negative evidence — the runtime marks those "(not observed)" and the
 *    absence-discipline governs them.
 */
export interface SignalScoring {
  /** What a present/true/high value ESTABLISHES as evidence (pt-BR). */
  licenses: string;
  /** The inflation bound: what the value does NOT establish (pt-BR). */
  doesNotImply: string;
  /** Non-obvious value meanings — enum/bucket semantics, `null` = unknown,
   *  `none` as evidenced absence, etc. Omit when the value is a plain bool. */
  valueSemantics?: string;
  /** Signal keys that are CORRELATED reads of the same underlying evidence —
   *  never count them as independent corroborators. */
  correlatedWith?: string[];
}

export interface SignalLabel {
  /** Short human title shown in the matrix ("what is being extracted"). */
  label: string;
  /** One-line description elaborating what the signal captures. */
  description: string;
  /** How the signal is produced. */
  measurement: SignalMeasurement;
  /** Grouping for organising the matrix into readable sections. */
  group: SignalGroup;
  /** Scoring-grade semantics for the LLM judge. Mandatory for any signal a
   *  model routes (gated by seed-matrix-prompt-coverage.test.ts). */
  scoring?: SignalScoring;
}

export const SIGNAL_GROUP_TITLES: Record<SignalGroup, string> = {
  founder: "Empreendedorismo / fase inicial",
  progression: "Progressão de carreira",
  management: "Gestão de pessoas e funções",
  stakeholder: "Agência sobre stakeholders",
  "product-systems": "Profundidade de produto / sistemas",
  business: "Acumen analítico / estratégico",
  hr: "Profundidade de RH",
  environment: "Ambiente / palco",
  academic: "Formação acadêmica",
  priors: "Priors de senioridade",
  impact: "Impacto quantificado",
  recognition: "Reconhecimento / produção intelectual",
  credentials: "Credenciais / idiomas",
  geography: "Alcance geográfico",
  flags: "Sinais estruturais / caps",
  "ic-depth": "Profundidade IC / analítica",
  "autonomy-depth": "Autonomia / autodireção",
  engineering: "Profundidade de engenharia",
};

export const SIGNAL_LABELS: Record<string, SignalLabel> = {
  // founder / early-stage
  max_founded_years: {
    label: "Anos como fundador confirmado",
    description:
      "Tempo total à frente de empresa fundada — classificador LLM separa fundadores reais de cargos pseudo-fundador.",
    measurement: "llm",
    group: "founder",
    scoring: {
      licenses:
        "Tempo contínuo à frente da fundação de MAIOR duração, com a fundação confirmada por um classificador que separa fundadores reais de cargos pseudo-fundador (ex.: 'Founding Partner' honorário). Estabelece a duração do episódio de fundação mais longo da carreira, não o total somado entre fundações.",
      doesNotImply:
        "Um valor alto não estabelece o resultado da empresa (sucesso, escala ou exit) — apenas o tempo à frente dela.",
      valueSemantics:
        "Toma o MAIOR entre os episódios de fundação confirmados (não a soma); 0 = nenhuma fundação passou pelo classificador — não distingue 'nunca fundou' de 'fundação não confirmada'.",
      correlatedWith: ["num_companies_founded"],
    },
  },
  num_companies_founded: {
    label: "Empresas fundadas",
    description: "Quantas empresas a pessoa fundou (apenas as confirmadas pelo classificador).",
    measurement: "llm",
    group: "founder",
    scoring: {
      licenses:
        "Quantidade de fundações CONFIRMADAS pelo classificador (que exclui títulos honoríficos ou pseudo-fundador) — estabelece que a pessoa estruturou mais de uma iniciativa empresarial do zero.",
      doesNotImply:
        "A contagem não estabelece porte, sucesso ou sobrevivência de cada empresa — mede apenas quantos episódios de fundação foram confirmados.",
      valueSemantics:
        "Cada unidade é uma fundação distinta que passou pelo classificador e teve duração mínima de 12 meses; fundações concorrentes/curtas contam igual a fundações consolidadas.",
      correlatedWith: ["max_founded_years"],
    },
  },
  earliest_employee_rank: {
    label: "Estágio mais inicial em que entrou (rank)",
    description:
      "Quão cedo a pessoa entrou na menor empresa da carreira. Rank maior = time menor / fase mais inicial. ⚠ Frequentemente NA — depende de companyStaffCountRange, que o actor do LinkedIn não fornece (não rotear como confiável).",
    measurement: "numeric",
    group: "founder",
    scoring: {
      licenses:
        "Rank ordinal do menor time em que a pessoa esteve durante a carreira — um rank alto estabelece proximidade estrutural a um estágio operacional muito inicial (early-stage).",
      doesNotImply:
        "Um rank alto não estabelece papel de liderança ou fundação — apenas o tamanho do time observado (staff count ATUAL da empresa, não histórico no momento da entrada).",
      valueSemantics:
        "Escala ordinal founder=5 > 1-20=4 > 21-50=3 > 51-100=2 > >100=1; depende de companyStaffCountRange, que o actor do LinkedIn raramente fornece — frequentemente null, e null não é evidência de carreira só em empresas grandes.",
      correlatedWith: ["earliest_employee_bucket", "years_at_small_non_vc_company"],
    },
  },
  earliest_employee_bucket: {
    label: "Estágio mais inicial em que entrou (faixa)",
    description:
      "Faixa do menor time em que esteve: founder / 1-20 / 21-50 / 51-100 / >100. ⚠ Frequentemente NA — depende de companyStaffCountRange, que o actor do LinkedIn não fornece (não rotear como confiável).",
    measurement: "numeric",
    group: "founder",
    scoring: {
      licenses:
        "Faixa nomeada do menor time em que a pessoa esteve — o mesmo fato que earliest_employee_rank expressa em rótulos; a faixa 'founder' reflete o mesmo classificador de fundação usado em num_companies_founded.",
      doesNotImply:
        "Uma faixa pequena não estabelece atuação como líder ou fundador dentro do time — apenas o porte da empresa no momento em que o dado de staff count foi capturado.",
      valueSemantics:
        "Ordem founder > 1-20 > 21-50 > 51-100 > >100, do mais early-stage ao maior; ausente na maioria dos perfis por falta de companyStaffCountRange — ausência não deve ser lida como 'nunca esteve em empresa pequena'.",
      correlatedWith: ["earliest_employee_rank", "years_at_small_non_vc_company"],
    },
  },
  years_at_small_non_vc_company: {
    label: "Anos em pequena empresa operacional",
    description:
      "Tempo em empresas pequenas (≤50) não-financeiras. Hoje frequentemente 0 — dado de staff count não é ingerido.",
    measurement: "numeric",
    group: "founder",
    scoring: {
      licenses:
        "Tempo cumulativo (união de intervalos por empregador, ≥6 meses cada) em empresas pequenas (staff count ≤50), excluindo intermediários financeiros (VC/PE/IB-boutique) e entidades estudantis — estabelece exposição operacional sustentada a ambiente de porte reduzido, próximo ao empreendedorismo.",
      doesNotImply:
        "Não estabelece papel de liderança ou fundação na empresa pequena — mede apenas tempo de presença nela.",
      valueSemantics:
        "Soma anos por empregador que passou o filtro (exclui financeiras e clubes/empresas juniores/tutoria); hoje frequentemente 0 porque depende de companyStaffCountRange, raramente presente no dado de origem — 0 não deve ser lido como 'nunca em empresa pequena'.",
      correlatedWith: ["earliest_employee_bucket", "earliest_employee_rank"],
    },
  },

  // progression
  num_strict_promotions: {
    label: "Promoções estritas",
    description:
      "Promoções dentro do mesmo empregador classificadas como reais (não laterais) pelo LLM strict.",
    measurement: "llm",
    group: "progression",
  },
  num_loose_promotions: {
    label: "Promoções (loose)",
    description:
      "Promoções dentro do mesmo empregador via classificador loose — leva em conta as descrições dos cargos (escopo, reports, P&L) além do título, e prefere PROMOTION em empate. Tende a contar mais promoções que o strict.",
    measurement: "llm",
    group: "progression",
    scoring: {
      licenses:
        "Cada promoção contada estabelece uma transição de título dentro do mesmo empregador que o classificador loose considerou avanço real de responsabilidade, a partir da descrição do cargo (reports, P&L, mandato) — não apenas um título mais bonito.",
      doesNotImply:
        "O classificador prefere PROMOTION em empates e decide pela escada de título quando a descrição é fraca — não trate a contagem como prova definitiva do tamanho do salto; corrobore com o rank de título ou evidência de escopo.",
      valueSemantics:
        "Tende a contar mais promoções que a métrica strict (deprecated) por ler as descrições e preferir PROMOTION em empate — números altos podem refletir o viés do classificador tanto quanto a carreira real.",
      correlatedWith: [
        "avg_loose_promo_interval_years",
        "num_employers_with_loose_promotion",
        "has_double_loose_promotion",
        "promotion_cadence_regularity",
      ],
    },
  },
  avg_promo_interval_years: {
    label: "Intervalo médio entre promoções (strict)",
    description: "Média de anos entre promoções strict. Menor = trajetória mais rápida.",
    measurement: "numeric",
    group: "progression",
  },
  avg_loose_promo_interval_years: {
    label: "Intervalo médio entre promoções (loose)",
    description: "Média de anos entre promoções loose. Menor = trajetória mais rápida.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Quando presente, estabelece o tempo médio entre o início do cargo anterior e o início do cargo alcançado, nas transições que o classificador loose considerou promoção — mede o ritmo de progressão intra-empregador nesses eventos.",
      doesNotImply:
        "Um valor baixo não estabelece por si só o patamar de responsabilidade atingido — mede apenas velocidade; corrobore com o rank de título para saber o degrau real.",
      valueSemantics:
        "Nulo quando nenhuma transição foi classificada como promoção loose (não evidencia estagnação — pode refletir carreira sem transições intra-empregador ou dados incompletos); calculado só sobre as promoções detectadas, então uma amostra pequena (ex.: uma única promoção) produz média pouco robusta.",
      correlatedWith: [
        "num_loose_promotions",
        "num_employers_with_loose_promotion",
        "has_double_loose_promotion",
        "promotion_cadence_regularity",
      ],
    },
  },
  max_anchor_promotion_count: {
    label: "Promoções no empregador-âncora",
    description: "Promoções dentro da empresa de maior tenure.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Um valor >0 estabelece que houve ao menos uma mudança de título dentro do empregador de maior permanência — evidência bruta de movimento no employer-âncora.",
      doesNotImply:
        "É uma aproximação por contagem de títulos distintos, não uma classificação de promoção real — não distingue avanço de mudança lateral; corrobore com num_loose_promotions, já validado por classificador, antes de tratar como prova de avanço de nível.",
      valueSemantics:
        "Contagem bruta (nº de títulos distintos no empregador-âncora menos 1), sem filtrar mudanças laterais nem validar se a mudança foi de fato uma promoção.",
      correlatedWith: ["max_anchor_years", "has_lock_in_pattern"],
    },
  },

  // management
  pm_llm_has_owned_function_at_scale: {
    label: "Dono de função em escala",
    description:
      "Foi dono de departamento/função (≥10 staff ou cross-funcional, ≥2 anos) em empresa complexa, com autoridade hire/fire/budget.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Estabelece ownership de um departamento/função (≥10 staff sob a pessoa OU ownership cross-funcional, ≥2 anos) numa empresa de complexidade operacional real (multinacional ≥1000 staff, ou scale-up Series A+ com ≥100 staff), com autoridade hire/fire/budget OU accountability ponta-a-ponta documentada. É o piso mais alto de escopo de gestão do catálogo.",
      doesNotImply:
        "Um título 'Manager' que é trilha sênior-IC (ex.: Sr Manager em consultoria, Sr Engineering Manager em trilha IC), liderança de projeto sem autoridade de staff, ou um cargo de diretor mantido ≤1 ano não bastam — nunca inferido, apenas evidência explícita de título + escala + tenure ou descrição de ownership.",
      valueSemantics:
        "Três-estados, explicit-evidence-only (NO-PRIOR): null = desconhecido (título ambíguo, escala invisível — o default para perfis finos); false só quando a carreira inteira e visível mostra nunca ter havido ownership de função em escala (ex.: escada IC-only completa).",
      correlatedWith: ["pm_llm_has_director_or_vp_title"],
    },
  },
  pm_llm_has_director_or_vp_title: {
    label: "Diretor / VP+ em empresa real",
    description:
      "Carregou título de Director / VP / Head / Chief por ≥2 anos em empresa ≥50 funcionários.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Estabelece que a pessoa carregou um título Director / VP / Head / Chief por ≥2 anos numa empresa real (≥50 funcionários) — julgamento conjunto de título + tenure + porte, não o título isolado.",
      doesNotImply:
        "CEO/CTO/COO de uma startup minúscula (≤30 funcionários) não conta aqui (esse sinal vive nos flags de founder) — não infira porte ou tenure a partir do título sozinho, e o sinal não estabelece ownership de função (ver pm_llm_has_owned_function_at_scale) nem escopo multi-time (ver pm_llm_has_multi_team_program_evidence).",
      valueSemantics:
        "Três-estados: false é legítimo quando a escada de títulos é visível e completa e nunca chega a Director+ (teto evidenciado); null quando porte da empresa ou identidade do empregador são ilegíveis com título ambíguo — não confunda com false.",
      correlatedWith: ["pm_has_executive_role", "pm_llm_has_owned_function_at_scale"],
    },
  },
  pm_llm_has_multi_team_program_evidence: {
    label: "Rodou programa multi-time",
    description:
      "Liderou iniciativa/programa atravessando ≥2 times ou funções por ≥1 trimestre. Inferível da estrutura quando há títulos de program/portfolio/PMO por ≥1 ano. null/FALSE = não evidenciado no perfil, nunca prova de ausência — não cite como evidência primária de nível baixo.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Estabelece que a pessoa rodou um programa/projeto atravessando ≥2 times ou funções por ≥1 trimestre — via descrição explícita OU, como structure-prior, um título Program/Portfolio/PMO/Project Manager mantido ≥1 ano numa organização real.",
      doesNotImply:
        "Liderança analítica isolada ('led analysis', 'led research') e ownership de projeto solo/single-team não bastam; o título sozinho licencia o structure-prior apenas quando mantido ≥1 ano numa organização real, e não deve ser confundido com ownership de função inteira (pm_llm_has_owned_function_at_scale, uma barra mais alta).",
      valueSemantics:
        "Três-estados: null = desconhecido, nunca evidência de ausência (perfil fino sem título de programa nem narrativa de programa fica null, não false).",
      correlatedWith: ["pm_has_program_or_pmo"],
    },
  },
  pm_llm_years_in_management: {
    label: "Anos em gestão (LLM)",
    description:
      "Anos com título de gerente-ou-superior e ao menos um reporte direto, deduplicados pelo LLM.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Soma os anos em que a pessoa carregou título de gerente-ou-superior COM reports diretos plausíveis, deduplicados por intervalos não sobrepostos. Títulos de gestão de pessoas em função de RH contam; papéis de consultoria como Engagement Manager / Project Leader / Case Team Lead contam pelo valor de face (liderar case teams é gerir execução).",
      doesNotImply:
        "Um título 'Manager' que é claramente trilha sênior-IC sem reports plausíveis (ex.: Product Manager sem reports descritos) não conta — anos aqui não equivalem a anos em qualquer cargo com a palavra 'manager' no título, essa é a leitura mais frouxa de pm_years_in_management.",
      valueSemantics:
        "Três-estados: 0 só quando a escada de títulos é visível e completa e claramente IC-only (evidência de ausência); null quando parte da história é datada mas ambígua sobre reports.",
      correlatedWith: ["pm_years_in_management", "pm_num_management_titles"],
    },
  },
  pm_years_in_management: {
    label: "Anos em cargos de gestão (título)",
    description:
      "Anos em cargos cujo título contém manager/gerente/head/director/coordenador/lead.",
    measurement: "keyword",
    group: "management",
    scoring: {
      licenses:
        "Soma os anos (união de intervalos por empregador) em cargos cujo título bate no mesmo regex de gestão/executivo/programa usado por pm_num_management_titles.",
      doesNotImply:
        "É extração puramente por palavra-chave no título — não verifica se havia reports diretos reais nem distingue gestão de pessoas de títulos IC-flavored ('Product Manager', 'Project Manager', 'Account Manager' contam igual); não trate como confirmação de gestão de pessoas sem corroborar com pm_llm_years_in_management ou pm_llm_has_director_or_vp_title.",
      valueSemantics:
        "Deduplicado por empregador (união de meses, não soma linhas sobrepostas), mas sem filtro de reports — ruído conhecido: sobrestima quando o título é IC-flavored ou quando a pessoa trocou de título várias vezes no mesmo cargo de fato.",
      correlatedWith: ["pm_num_management_titles", "pm_llm_years_in_management"],
    },
  },
  pm_num_management_titles: {
    label: "Quantos cargos de gestão",
    description: "Contagem de cargos distintos com título de gestão.",
    measurement: "keyword",
    group: "management",
    scoring: {
      licenses:
        "Conta quantos cargos distintos na carreira batem no regex de título de gestão (manager/gerente/head/director/diretor/líder/leader/coordenador/coordinator/lead OU os tokens executivos OU os tokens de programa/PMO).",
      doesNotImply:
        "Conta cargos, não organizações lideradas nem reports reais — troca de título dentro do MESMO empregador (Manager → Senior Manager → Director) soma múltiplas entradas mesmo sendo uma única passagem de gestão contínua, e títulos IC-flavored como 'Product Manager' ou 'Project Manager' entram no mesmo regex sem exigir pessoas subordinadas.",
      valueSemantics:
        "Contagem simples de cargos que batem no regex (measurement: keyword) — não deduplica por empregador nem verifica reports; ruído conhecido: carreiras curtas com trocas de título frequentes inflam a contagem sem indicar mais senioridade.",
      correlatedWith: [
        "pm_years_in_management",
        "pm_llm_years_in_management",
        "pm_has_executive_role",
        "pm_has_program_or_pmo",
      ],
    },
  },
  pm_has_executive_role: {
    label: "Cargo executivo (VP/Chief/Founder)",
    description: "Algum cargo com título VP / Chief / Founder / Partner.",
    measurement: "keyword",
    group: "management",
    scoring: {
      licenses:
        "Indica que ao menos um cargo da carreira carrega um dos tokens de título executivo (VP / vice-president / chief / c-qualquer-o / founder / co-founder / partner / sócio) — um proxy de vocabulário para presença de título executivo em algum ponto da carreira.",
      doesNotImply:
        "É um match de regex sobre o TÍTULO apenas — não verifica porte da empresa, tenure mínima nem se a autoridade era real; um 'Founder' de uma venture de 3 pessoas ou um VP por 6 meses batem igual a um VP+ estabelecido, e não deve ser tratado como equivalente a pm_llm_has_director_or_vp_title, que exige tenure e porte.",
      correlatedWith: [
        "pm_num_management_titles",
        "pm_years_in_management",
        "pm_llm_has_director_or_vp_title",
      ],
    },
  },
  pm_has_program_or_pmo: {
    label: "Cargo de Programa / PMO",
    description:
      "Algum cargo com título program / portfolio / PMO / project-manager / product-owner.",
    measurement: "keyword",
    group: "management",
    scoring: {
      licenses:
        "Indica que ao menos um cargo da carreira carrega um dos tokens de título de programa (program / portfolio / PMO / project-manager / gerente-de-projeto / product-owner) — um proxy de vocabulário para exposição a papéis de coordenação de programa.",
      doesNotImply:
        "É um match de regex sobre o TÍTULO apenas — não confirma que o programa cruzou múltiplos times/funções nem por quanto tempo; 'Project Manager' aparece aqui mesmo quando o papel era execução de um único time, e não deve ser tratado como equivalente a pm_llm_has_multi_team_program_evidence, que exige a evidência multi-time.",
      correlatedWith: [
        "pm_num_management_titles",
        "pm_years_in_management",
        "pm_llm_has_multi_team_program_evidence",
      ],
    },
  },

  // stakeholder
  sm_llm_has_executive_stakeholder_influence: {
    label: "Drove decisões com C-suite",
    description:
      "Moveu decisões envolvendo C-suite (CEO/CFO/CTO/COO/CHRO/board) por ≥2 anos — não apenas 'apresentou para'.",
    measurement: "llm",
    group: "stakeholder",
    scoring: {
      licenses:
        "Um true estabelece que o candidato CONDUZIU decisões envolvendo C-suite (CEO/CFO/CTO/COO/CHRO/board) por ≥2 anos, com posse explícita de um resultado — não apenas apresentação ou assessoria ao executivo.",
      doesNotImply:
        "Um título de proximidade executiva (Chief of Staff, VP reportando ao founder) por si só não estabelece que o candidato conduziu decisões — proximidade e condução são fatos distintos; título sozinho vira sm_llm_years_at_executive_proximity, não este sinal.",
      valueSemantics:
        "NO-PRIOR (piso alto): nunca inferido de título, tenure ou firma; requer evidência textual explícita de engajamento direto e posse do resultado. Ausência de narrativa é null, nunca false.",
      correlatedWith: [
        "sm_llm_years_at_executive_proximity",
        "sm_has_exec_liaison",
        "sm_llm_has_driven_decisions_against_pushback",
      ],
    },
  },
  sm_llm_has_cross_functional_alignment_evidence: {
    label: "Liderou alinhamento cross-funcional",
    description:
      "Liderou iniciativa cross-funcional onde o trabalho ERA o alinhamento entre funções.",
    measurement: "llm",
    group: "stakeholder",
    scoring: {
      licenses:
        "Um true estabelece que o candidato liderou uma iniciativa cruzando ≥2 funções por ≥1 trimestre onde o próprio trabalho ERA o alinhamento entre áreas — não liderança técnica de uma função só.",
      doesNotImply:
        'Exposição a múltiplas áreas ("trabalhei com vários times") não estabelece que o candidato possuía o alinhamento em si — sem descrição narrando a posse do alinhamento, o sinal não dispara.',
      valueSemantics:
        "Sinal gated por descrição, sem prior estrutural — perfis finos com descrições vazias resolvem para null, nunca false; false só quando a descrição substantiva mostra execução de função única ao longo de toda a carreira.",
      correlatedWith: ["pm_llm_has_multi_team_program_evidence"],
    },
  },
  sm_llm_has_driven_decisions_against_pushback: {
    label: "Moveu decisão contra oposição",
    description:
      "Evidência explícita de ter convencido / negociado / desbloqueado decisão sob resistência. O sinal mais forte de agência.",
    measurement: "llm",
    group: "stakeholder",
    scoring: {
      licenses:
        "Um true estabelece evidência explícita de ter movido uma decisão adiante contra resistência real (convenceu / negociou / desbloqueou apesar de veto ou prioridades concorrentes) — o sinal mais forte de agência sobre stakeholders.",
      doesNotImply:
        'Verbos genéricos de liderança ("liderei", "gerenciei") sem menção a oposição não estabelecem este sinal — a resistência precisa ser parte explícita do relato, não presumida pelo cargo.',
      valueSemantics:
        "NO-PRIOR (piso alto): nunca inferido de título, tenure ou firma — só evidência textual explícita licencia true; ausência de narrativa é null, nunca false (false é reservado para perfis onde toda a carreira narrada mostra ausência clara de episódios de decisão, o que é raro).",
      correlatedWith: ["sm_llm_has_executive_stakeholder_influence"],
    },
  },
  sm_llm_years_at_executive_proximity: {
    label: "Anos reportando a executivo",
    description: "Anos em cargo que reporta diretamente a C-suite (ou sendo C-suite).",
    measurement: "llm",
    group: "stakeholder",
    scoring: {
      licenses:
        "Um valor > 0 estabelece quantos anos o candidato esteve em cargo que reporta diretamente a um executivo C-level (ou sendo ele próprio C-suite) — reporte direto, não reporte-de-reporte.",
      doesNotImply:
        "Anos de proximidade executiva não estabelecem que o candidato influenciou ou conduziu decisões — proximidade é exposição estrutural ao cargo, não agência sobre o que foi decidido (ver sm_llm_has_executive_stakeholder_influence para agência).",
      valueSemantics:
        "Inferível de estrutura (títulos Chief of Staff / Executive Assistant ao CEO / qualquer título C-suite / founder-CEO licenciam o valor mesmo com descrição vazia). 0 é evidência de ausência apenas quando o histórico datado completo mostra cargos júnior/médio em empresas grandes onde reporte direto a C-suite é estruturalmente implausível; null quando a linha de reporte é simplesmente invisível.",
      correlatedWith: ["sm_has_exec_liaison", "sm_llm_has_executive_stakeholder_influence"],
    },
  },
  sm_llm_has_accelerated_growth_track: {
    label: "Crescimento acelerado num empregador",
    description:
      "Dois bumps de título em ≤3 anos no mesmo empregador, ou expansão documentada de escopo.",
    measurement: "llm",
    group: "stakeholder",
    scoring: {
      licenses:
        "Um true estabelece crescimento de escopo mais rápido que o padrão dentro de UM MESMO empregador — dois saltos de título em ≤3 anos, ou expansão documentada de remit (assumiu região/workstream adicional) dentro da mesma empresa.",
      doesNotImply:
        "Crescimento acelerado num único empregador não estabelece senioridade absoluta nem influência sobre stakeholders — é velocidade de progressão local, não patamar atingido.",
      valueSemantics:
        "false é legítimo (evidência de ausência) quando o histórico datado completo de títulos numa mesma empresa não mostra saltos — mas exige datas visíveis; sem datas, é null. Saltos entre empregadores diferentes não contam para este sinal (é sobre crescimento dentro de uma empresa, não sobre mudança de emprego).",
      correlatedWith: [
        "num_loose_promotions",
        "has_double_loose_promotion",
        "promotion_cadence_regularity",
      ],
    },
  },
  sm_has_exec_liaison: {
    label: "Cargo Head / Senior-Manager / Board",
    description: "Título contém executive / board / leadership-team / head-of / senior-manager.",
    measurement: "keyword",
    group: "stakeholder",
    scoring: {
      licenses:
        "Título contém termo de proximidade executiva (executive / board / leadership-team / head-of / senior-manager) — corrobora que o cargo tinha visibilidade ou nomenclatura de proximidade com liderança sênior.",
      doesNotImply:
        "Bater a regex de título não estabelece influência sobre decisões executivas nem trato direto com C-suite — é só o rótulo do cargo, não o conteúdo do trabalho.",
      valueSemantics:
        "Sinal keyword deprecado (substituído por sm_llm_has_executive_stakeholder_influence); false/ausência de match nunca deve ser lida como evidência negativa de proximidade executiva, só como 'título não bateu o regex'.",
      correlatedWith: [
        "sm_llm_has_executive_stakeholder_influence",
        "sm_llm_years_at_executive_proximity",
      ],
    },
  },
  sm_years_client_facing: {
    label: "Anos client-facing",
    description: "Anos em cargos de cliente / account / parceria.",
    measurement: "keyword",
    group: "stakeholder",
    scoring: {
      licenses:
        "O valor estabelece quantos anos o candidato passou em cargos com título de cliente / account / consultoria — tempo estrutural de exposição a interlocutores externos, unindo intervalos sobrepostos por empregador.",
      doesNotImply:
        "Anos em título client-facing não estabelecem que o candidato conduziu decisões ou teve agência sobre stakeholders — é tempo de exposição por título, não profundidade de influência (ver a família sm_llm_* para agência real).",
      valueSemantics:
        "Métrica keyword sobre título, não sobre conteúdo da descrição; carreiras com múltiplos cargos curtos e sobrepostos no mesmo empregador já são deduplicadas por união de intervalos, mas o regex pode capturar títulos client-facing de baixa profundidade (ex.: analista júnior de contas) junto com os de alta.",
      correlatedWith: ["sm_num_client_facing_roles"],
    },
  },
  sm_num_client_facing_roles: {
    label: "Quantos cargos client-facing",
    description: "Contagem de cargos com título client / account / partnership.",
    measurement: "keyword",
    group: "stakeholder",
    scoring: {
      licenses:
        "A contagem estabelece quantos cargos distintos do candidato tinham título de cliente / account / parceria (account manager, client success, partnerships) — exposição estrutural a interlocutores externos.",
      doesNotImply:
        "O número de cargos client-facing não estabelece senioridade nem agência sobre decisões do cliente — é contagem de títulos batendo um regex, não profundidade de relacionamento ou de influência.",
      valueSemantics:
        "Contagem simples de posições que batem o padrão de título; carreiras com cargos curtos ou sobrepostos podem inflar a contagem sem indicar tempo real acumulado (ver sm_years_client_facing para a métrica temporal).",
      correlatedWith: ["sm_years_client_facing"],
    },
  },

  // product-systems
  ps_llm_has_systems_thinking_evidence: {
    label: "Desenhou plataforma multi-sistema em escala",
    description:
      "Conectou múltiplos sistemas/fluxos em escala significativa (plataforma multi-sistema, VP+ de produto/eng em empresa ≥50, founder VC-backed em escala).",
    measurement: "llm",
    group: "product-systems",
    scoring: {
      licenses:
        "Um true estabelece desenho de soluções conectando MÚLTIPLOS sistemas ou fluxos em escala significativa — plataforma multi-sistema, VP/Head/Chief de Produto ou Engenharia com escopo multi-time (≥50 pessoas), ou co-founder de startup de produto VC-backed em escala relevante.",
      doesNotImply:
        "Um cargo sênior de produto isolado, sem escopo multi-sistema explícito, não estabelece este sinal — carreira de produto longa e com aparência sênior mas com descrições vazias não prova conexão de múltiplos sistemas em escala.",
      valueSemantics:
        "NO-PRIOR (piso alto, ancora patamares elevados): nunca inferido por estrutura sozinha — exige fato explícito de multi-sistema-em-escala nas descrições, ou título qualificador explícito + porte de empresa comprovado. false só quando o perfil é substantivo e evidentemente de escopo único/pequeno ao longo de toda a carreira (ex.: ladder júnior explícito); null sempre que o escopo é ilegível.",
      correlatedWith: ["max_founded_years", "num_companies_founded"],
    },
  },
  ps_llm_has_solution_design_evidence: {
    label: "Desenhou produto/sistema end-to-end",
    description:
      "Desenhou solução real shipada end-to-end — escolheu ferramentas, fluxos, decisões arquiteturais.",
    measurement: "llm",
    group: "product-systems",
    scoring: {
      licenses:
        "Um true estabelece que o candidato desenhou uma solução end-to-end num produto ou sistema real shipado — escolheu ferramentas, desenhou fluxos, tomou decisões arquiteturais — não apenas assessorou ou revisou o desenho de terceiros.",
      doesNotImply:
        "Um título de Product Manager ou Solutions Architect isolado, sem tenure sustentado ou descrição, não estabelece autoria de desenho — o cargo por si só não prova que o candidato desenhou algo shipado.",
      valueSemantics:
        "Gated por descrição, EXCETO um prior estrutural licenciado: track sustentado de ≥2 anos em título de product owner/solutions architect em firma séria licencia true mesmo com descrição vazia. Abaixo desse patamar (PM júnior de 1 ano, consultoria/PM sem menção a criação), descrição vazia resolve para null, nunca false; false só quando a descrição substantiva mostra trabalho puramente assessorial/executório, ou a carreira é evidentemente livre de desenho.",
      correlatedWith: ["ps_llm_product_or_systems_practice_years", "ps_years_in_product"],
    },
  },
  ps_llm_has_structured_thinking_evidence: {
    label: "Criou frameworks reusáveis",
    description:
      "Criou templates / frameworks / processos reusáveis como saídas product-like (genuinamente reutilizados).",
    measurement: "llm",
    group: "product-systems",
    scoring: {
      licenses:
        "Um true estabelece que o candidato criou templates, frameworks ou processos repetíveis que foram genuinamente REUTILIZADOS — simplificou complexidade em formato escalável, não apenas produziu um artefato único.",
      doesNotImply:
        "Um cargo de consultoria ou produto por si só não estabelece criação de framework reutilizado — a reutilização é invisível em títulos e não deve ser inferida de cargo ou tier de firma.",
      valueSemantics:
        "Sinal gated por descrição, sem prior estrutural em nenhum caso — descrições vazias ou silenciosas sobre reutilização resolvem para null, nunca false; false é reservado para descrições substantivas que mostram trabalho puramente executório, nunca estruturante.",
    },
  },
  ps_llm_product_or_systems_practice_years: {
    label: "Anos em prática de produto/sistemas (LLM)",
    description:
      "Anos em que desenhar ou ser dono de produtos/sistemas/processos era responsabilidade primária.",
    measurement: "llm",
    group: "product-systems",
    scoring: {
      licenses:
        "O valor estabelece quantos anos desenhar ou ser dono de produtos/sistemas/processos foi a responsabilidade PRIMÁRIA do candidato — soma de intervalos não sobrepostos em cargos de product manager/owner/designer, solutions/systems architect, founder que desenhou o produto, ou ops/process-design cuja saída eram sistemas reutilizáveis.",
      doesNotImply:
        "Anos acumulados nesta métrica não estabelecem profundidade ou escala do trabalho de produto/sistemas — anos são inferíveis por título sozinho, então o valor mede tempo em cargo, não a complexidade do que foi desenhado (ver os flags ps_llm_has_* para o nível de sofisticação).",
      valueSemantics:
        "Inferível por título (intervalos com título de produto/arquiteto/founder contam mesmo com descrição vazia). 0 é evidência de ausência apenas quando toda a carreira é evidentemente não-produto (finanças, vendas, consultoria genérica sem nada product-shaped); null quando os títulos são injulgáveis (cargos genéricos que poderiam esconder trabalho de produto).",
      correlatedWith: [
        "ps_years_in_product",
        "ps_years_in_engineering_or_design",
        "ps_llm_has_solution_design_evidence",
      ],
    },
  },
  ps_years_in_product: {
    label: "Anos em produto (título)",
    description: "Anos em cargos com título product manager / owner / similar.",
    measurement: "keyword",
    group: "product-systems",
    scoring: {
      licenses:
        "O valor estabelece quantos anos o candidato esteve em cargos com título de produto (product manager, product owner e variantes) — tempo estrutural de exposição à função de produto, unindo intervalos sobrepostos por empregador.",
      doesNotImply:
        "Anos em título de produto não estabelecem que o candidato desenhou ou possuiu soluções end-to-end — é tempo por título, não evidência comportamental de desenho (ver ps_llm_has_solution_design_evidence e ps_llm_has_systems_thinking_evidence para isso).",
      valueSemantics:
        'Sinal deprecado, substituído por ps_llm_product_or_systems_practice_years, que amplia a mesma métrica de anos com título-inferência mais ampla (inclui architect/founder/ops-design); métrica puramente keyword — um título "Product Analyst" júnior de curta duração conta igual a um "Head of Product" sênior.',
      correlatedWith: [
        "ps_llm_product_or_systems_practice_years",
        "ps_years_in_engineering_or_design",
      ],
    },
  },
  ps_years_in_engineering_or_design: {
    label: "Anos em engenharia ou design",
    description: "Anos em cargos com título engineering / design.",
    measurement: "keyword",
    group: "product-systems",
    scoring: {
      licenses:
        "O valor estabelece quantos anos o candidato esteve em cargos com título de engenharia ou design (engineer, developer, designer, UX/UI) — tempo estrutural de exposição a trabalho técnico ou de design, unindo intervalos sobrepostos por empregador.",
      doesNotImply:
        "Anos em título de engenharia/design não estabelecem prática de produto ou pensamento sistêmico — é tempo por título, não evidência de que o candidato desenhou soluções ou possuiu produtos (ver a família ps_llm_* para essas evidências comportamentais).",
      valueSemantics:
        "Sinal deprecado, substituído por ps_llm_product_or_systems_practice_years, que cobre o mesmo território de evidência com julgamento mais rico; métrica puramente keyword sobre título — cargos técnicos júnior ou de curta duração contam com o mesmo peso que cargos seniores.",
      correlatedWith: ["ps_llm_product_or_systems_practice_years", "ps_years_in_product"],
    },
  },

  // business / analytical
  ba_years_in_strategy: {
    label: "Anos em estratégia",
    description: "Anos em cargos com título strategy / business-development / GTM.",
    measurement: "keyword",
    group: "business",
    scoring: {
      licenses:
        "Estabelece quantos anos cumulativos a pessoa teve cargos cujo título casa com um padrão de estratégia/desenvolvimento de negócios (strategy, business development, growth, go-to-market, operations strategy).",
      doesNotImply:
        "Anos em título de estratégia não estabelecem profundidade de decisão nem ownership real de resultado — é leitura de título, não de conteúdo do trabalho.",
      valueSemantics:
        "Contagem por casamento de título (regex), não julgamento semântico do trabalho — cargos de 'growth marketing' também entram nesse padrão.",
      correlatedWith: ["analytical_years"],
    },
  },
  ba_years_in_finance: {
    label: "Anos em finanças",
    description: "Anos em cargos com título IB / M&A / equity-research / FP&A / financial-analyst.",
    measurement: "keyword",
    group: "business",
    scoring: {
      licenses:
        "Estabelece quantos anos cumulativos (união de intervalos por empregador) a pessoa teve cargos cujo título casa com um padrão de finanças (IB, M&A, equity research, valuations, FP&A, financial analyst, tesouraria, controladoria).",
      doesNotImply:
        "Anos acumulados não estabelecem execução de transações nem elite de firma — é só exposição de título; para execução real corrobore com has_executed_transactions.",
      valueSemantics:
        "Contagem por casamento de título (regex sobre título+empresa), não por julgamento de conteúdo do trabalho — cargos com título ambíguo em empresa não-financeira ficam de fora; sobreposições no mesmo empregador são deduplicadas.",
      correlatedWith: ["analytical_years", "has_executed_transactions", "ba_has_top_finance"],
    },
  },
  ba_has_top_finance: {
    label: "Trabalhou em finanças de elite",
    description: "Algum cargo com título IB / M&A / equity-research / valuations / FP&A.",
    measurement: "keyword",
    group: "business",
    scoring: {
      licenses:
        "Um true estabelece que o nome do empregador bate contra uma lista fixa e fechada de marcas de elite em finanças/IB/VC (bulge-bracket, fundos reconhecidos) em algum cargo da carreira.",
      doesNotImply:
        "False não estabelece ausência de trabalho de finanças de alto nível — bancos e fundos fora da lista fixa retornam false do mesmo jeito; é um match de marca, não um julgamento geral da seriedade da trilha.",
      valueSemantics:
        "Match de string contra lista fechada — não captura elite regional/boutique não listada; combine com ba_years_in_finance e has_executed_transactions para o quadro completo da trilha.",
      correlatedWith: [
        "has_executed_transactions",
        "transaction_scale",
        "top_consulting_track_level",
      ],
    },
  },
  ba_has_top_consulting: {
    label: "Trabalhou em consultoria de marca (MBB/Big-4)",
    description:
      "Nome do EMPREGADOR bate numa lista fixa de marcas (McKinsey/BCG/Bain/Big-4). FALSE não significa ausência de consultoria de alto nível — boutiques e consultorias fora da lista retornam FALSE; combine com anos de trabalho analítico e tier de pós-graduação para inferir o calibre de consultorias desconhecidas.",
    measurement: "keyword",
    group: "business",
    scoring: {
      licenses:
        "Um true estabelece que o nome do empregador bate contra uma lista fixa e fechada de marcas de consultoria de elite (McKinsey/BCG/Bain/Big-4) em algum cargo da carreira.",
      doesNotImply:
        "False não estabelece ausência de consultoria de alto nível — boutiques e consultorias fora da lista fixa retornam false do mesmo jeito; combine com anos analíticos e tier de pós-graduação para inferir o calibre de firmas desconhecidas.",
      valueSemantics:
        "É um match de string contra lista fechada de marcas, não um julgamento de seriedade da firma — uma boutique séria fora da lista sempre retorna false mesmo com prestígio comparável.",
      correlatedWith: ["top_consulting_track_level", "consulting_engagement_leadership"],
    },
  },
  ba_has_finance_field: {
    label: "Formação em finanças / economia",
    description: "Curso de formação em finanças, economia, contabilidade ou correlato.",
    measurement: "keyword",
    group: "business",
    scoring: {
      licenses:
        "Um true estabelece que o campo de formação acadêmica da pessoa casa com finanças, economia, administração, contabilidade ou área correlata (engenharia de produção, data science, estatística).",
      doesNotImply:
        "A formação não estabelece nenhuma experiência profissional em finanças nem profundidade analítica prática — é só sinal de educação, corrobore com ba_years_in_finance ou analytical_years para atuação real.",
    },
  },

  // HR
  hf_llm_has_hr_director_or_higher: {
    label: "Diretor de RH ou superior",
    description:
      "Carregou título HR Director / VP People / Head of People (em escala) / CHRO / CPO por ≥2 anos.",
    measurement: "llm",
    group: "hr",
    scoring: {
      licenses:
        "Estabelece que a pessoa carregou um título Director-ou-superior de RH (HR Director / VP People / Head of People em escala / CHRO / CPO, ou founder de empresa de domínio RH em escala) por ≥2 anos — é o piso mais alto da escala de RH do catálogo.",
      doesNotImply:
        "Senior HRBP / Sr Comp Analyst são títulos sênior-IC, não diretoria — não contam; e 'HR Manager'/'People Manager' sem qualificador Director também não conta. Nunca inferido de uma trajetória que 'provavelmente' terminaria em diretoria — exige título+tenure explícitos nas linhas de cargo.",
      valueSemantics:
        "Três-estados, explicit-evidence-only: false quando a escada de RH é explícita e visível e termina abaixo de Director (ex.: HR Analyst → HRBP → Senior HRBP, sem linha de diretor); null para qualquer ambiguidade (porte da empresa ilegível, 'Head of People' em empresa de escala desconhecida) — nunca confunda com false.",
      correlatedWith: ["hf_llm_hr_function_years", "hf_llm_has_run_multiple_hr_subsystems"],
    },
  },
  hf_llm_has_run_multiple_hr_subsystems: {
    label: "Rodou múltiplos sub-sistemas de RH",
    description:
      "Foi dono de ≥2 sub-sistemas de RH (comp, TA, perf, L&D, org design, HRBP) por múltiplos anos em escala.",
    measurement: "llm",
    group: "hr",
    scoring: {
      licenses:
        "Estabelece ownership pessoal de ≥2 sub-sistemas distintos de RH (compensação, aquisição de talento, performance, L&D, org design, HRBP, workforce mgmt) por múltiplos anos em empresas de complexidade operacional real — OU founder/CEO de empresa de domínio RH em escala (Series A+ de VCs sérias, ou ≥100 clientes enterprise) — OU ≥3 anos numa consultoria de RH séria com títulos que indicam trânsito por múltiplos sub-sistemas.",
      doesNotImply:
        "Longa permanência num único sub-sistema (ex.: 8 anos só de HRBP, ou 5 anos só de compensação numa firma tier-1) é o padrão de rejeição canônico do CHRO — tenure longa sozinha não estabelece amplitude, e uma rotação de 1 ano por 5 sub-funções também não estabelece profundidade suficiente.",
      valueSemantics:
        "Três-estados: false é legítimo quando a sequência de títulos mostra positivamente um único sub-sistema sustentado (ex.: 'HRBP, multinacional, 2014–2022' sem rotação); null quando um título genérico ('People Manager', 'HR Generalist') não revela quais sub-sistemas foram de fato geridos.",
      correlatedWith: ["hf_llm_hr_function_years", "hf_llm_has_hr_director_or_higher"],
    },
  },
  hf_llm_has_extensive_people_management: {
    label: "Gestão extensa de pessoas (não-RH)",
    description: "Geriu pessoas diretamente como gerente não-RH por ≥3 anos.",
    measurement: "llm",
    group: "hr",
    scoring: {
      licenses:
        "Estabelece que a pessoa geriu pessoas diretamente como gestora NÃO-RH por ≥3 anos — engineering manager, ops director, partner de consultoria com responsabilidade de time, founder/CEO de empresa não-RH com time real (≥10 staff), ou liderança comercial/vendas em multinacionais/scale-ups de peso por ≥3 anos. É o sinal que distingue 'geriu gente, nunca em RH' de 'nenhuma exposição a pessoas'.",
      doesNotImply:
        "Carreiras IC de finanças (IB analyst, PE associate, VC analyst, M&A) e trilhas de consultoria júnior (Associate/Consultant <4 anos, mesmo em MBB) são trabalho analítico individual, não gestão — não conte títulos ambíguos sobre reports ('Project Manager', 'Account Manager') sem confirmação, e este sinal não substitui nem se soma a experiência de RH (ver hf_llm_hr_function_years).",
      valueSemantics:
        "Três-estados: false quando a estrutura mostra positivamente uma trilha analítica IC pura (ex.: M&A Associate → VP em bancos, 9 anos); null quando o título é ambíguo sobre a existência de reports e nada resolve (ex.: 'Project Manager' 4 anos numa firma média desconhecida, sem descrição).",
    },
  },
  hf_llm_hr_function_years: {
    label: "Anos em RH como função primária",
    description:
      "Anos em que RH era o trabalho primário (HRBP, comp, recruiting, consultoria de RH, etc.).",
    measurement: "llm",
    group: "hr",
    scoring: {
      licenses:
        "Soma os anos (intervalos não sobrepostos) em que RH foi a função PRIMÁRIA da pessoa — cargos com título de RH (HRBP, Compensation Manager, Recruiter, Head of People, L&D Manager) ou consultoria de RH em tempo integral numa firma do domínio (Mercer, WTW, Aon, Korn Ferry, Comp, EY People, Deloitte HC). Títulos de RH são estrutura confiável, inferível mesmo com descrição vazia.",
      doesNotImply:
        "Trabalhar numa empresa que TEM uma área de RH não conta (ex.: analista de banco não vira RH só por a empresa ter um RH), nem contato indireto via parcerias/back-office; e gestão do próprio time como gestor não-RH também não conta aqui (isso vive em hf_llm_has_extensive_people_management).",
      valueSemantics:
        "Três-estados: 0 = ausência evidenciada (carreira inteira e visivelmente sem cargos de RH, ex.: 10 anos de IB Analyst → VP M&A); null = títulos genuinamente inclassificáveis ('Analyst'/'Consultant' sem firma ou contexto para saber se o caseload era RH) — nunca confunda os dois.",
      correlatedWith: ["hf_llm_has_run_multiple_hr_subsystems", "hf_llm_has_hr_director_or_higher"],
    },
  },

  // environment
  cum_T1_years: {
    label: "Anos em empresas Tier-1 elite",
    description:
      "Anos cumulativos (união de intervalos) em empresas Tier-1 segundo o catálogo de mercado.",
    measurement: "numeric",
    group: "environment",
    scoring: {
      licenses:
        "Estabelece quantos anos cumulativos (união de intervalos, sem dupla contagem de sobreposição) a trajetória passou em empregadores classificados Tier-1 — bar de contratação extremamente elevado (MBB, IB bulge-bracket, Big Tech, LatAm tech em janela de hipercrescimento, VC global reconhecido) segundo o classificador de mercado.",
      doesNotImply:
        "Anos em Tier-1 não estabelecem o nível do cargo exercido ali (júnior vs. sênior) nem desempenho da pessoa — mede o bar do empregador, não o que a pessoa fez nele.",
      valueSemantics:
        "A classificação de tier depende de função (cargo de linha vs. suporte, que pode subir ou descer um tier) e de era (empresas como Nubank/Stone/iFood têm janelas de tier específicas por data) — o mesmo empregador pode contar Tier-1 num período e não noutro; anos são somados por união de intervalo dentro do mesmo tier, então sobreposições no mesmo empregador não inflam o total.",
      correlatedWith: ["cum_T2_years"],
    },
  },
  cum_T2_years: {
    label: "Anos em empresas Tier-2",
    description: "Anos cumulativos em empresas Tier-2 (alto sinal, abaixo do apex).",
    measurement: "numeric",
    group: "environment",
    scoring: {
      licenses:
        "Estabelece quantos anos cumulativos a trajetória passou em empregadores classificados Tier-2 — bar elevado, ambiente de crescimento consistente (Big-4, LatAm tech madura/pós-janela, multinacionais de consumo/industrial em cargo de linha) segundo o mesmo classificador usado para cum_T1_years.",
      doesNotImply:
        "Anos em Tier-2 não estabelecem senioridade do cargo nem desempenho da pessoa — mede o bar do empregador, e um cargo de suporte no mesmo empregador pode ser rebaixado a Tier-3.",
      valueSemantics:
        "Mesma lógica de era/função de cum_T1_years: um cargo de linha em multinacional global (PepsiCo, Unilever etc.) sobe a Tier-2 mesmo quando a função isolada seria Tier-3; um cargo de suporte no mesmo tipo de empresa cai um tier.",
      correlatedWith: ["cum_T1_years"],
    },
  },

  // academic
  best_uni_tier: {
    label: "Tier da melhor universidade (graduação)",
    description: "Tier 1–5 da melhor universidade de graduação. Catálogo + fallback LLM.",
    measurement: "llm",
    group: "academic",
    scoring: {
      licenses:
        "Tier (catálogo de mercado + fallback LLM) da melhor instituição de GRADUAÇÃO — estabelece o rigor de seleção no ingresso à graduação.",
      doesNotImply:
        "O tier não estabelece desempenho, conclusão com distinção ou competência atual — é proxy de seleção institucional no ingresso.",
      valueSemantics:
        "Escala 1–5 em passos de 0.5 (5 = apex global, 4 = Tier-1 BR, 3.5 = Tier-1.5 BR/FGV, 3 = Tier-2, 2 = Tier-3, 1 = Tier-4/não reconhecida); null quando nenhuma entrada de graduação foi classificável — não interpretar como 'sem graduação'.",
      correlatedWith: ["best_grad_tier", "course_x_institution_tier", "has_competitive_course"],
    },
  },
  best_grad_tier: {
    label: "Tier da melhor universidade (pós)",
    description:
      "Tier 1–5 da melhor universidade de pós-graduação (5 = apex global; dispara também para programas em andamento). Catálogo + fallback LLM. Sozinho é proxy de plausibilidade; em CONJUNÇÃO com anos de trabalho analítico/consultoria, retro-valida a barra dos empregadores sem marca (programas apex selecionam pela qualidade do empregador).",
    measurement: "llm",
    group: "academic",
    scoring: {
      licenses:
        "Tier da melhor instituição de PÓS-graduação (mestrado, doutorado ou MBA classificado) — em conjunção com anos de trabalho analítico/consultoria, corrobora a barra de empregadores sem marca reconhecida (programas apex selecionam pela qualidade do empregador).",
      doesNotImply:
        "Isoladamente não estabelece senioridade ou impacto — é proxy de plausibilidade que só retro-valida a barra de empregador quando combinado com evidência de anos de atuação analítica/consultoria.",
      valueSemantics:
        "Mesma escala 1–5 de best_uni_tier; dispara também para programas em andamento; null quando não há entrada de pós classificável — não confundir com 'nunca fez pós'.",
      correlatedWith: ["best_uni_tier", "course_x_institution_tier", "has_masters", "has_phd"],
    },
  },
  has_phd: {
    label: "Tem PhD",
    description: "Tem doutorado.",
    measurement: "keyword",
    group: "academic",
    scoring: {
      licenses:
        "A pessoa concluiu ou está cursando doutorado — estabelece um episódio de formação de doutorado.",
      doesNotImply:
        "Não estabelece a instituição, o tema ou a aplicação prática da pesquisa à atuação profissional fora da academia.",
      valueSemantics:
        "Keyword match ('phd', 'ph.d', 'doutorado', 'doctor') no campo curso de qualquer entrada educacional; sem gate de duração — um doutorado em andamento também dispara.",
      correlatedWith: ["best_grad_tier"],
    },
  },
  has_masters: {
    label: "Tem mestrado",
    description: "Tem mestrado (≥1.5 anos de duração quando datado).",
    measurement: "keyword",
    group: "academic",
    scoring: {
      licenses:
        "A pessoa concluiu um mestrado — estabelece um episódio de formação de pós-graduação com duração mínima defensável.",
      doesNotImply:
        "Não estabelece o tier ou reconhecimento da instituição (isso é best_grad_tier) nem aplicação prática do mestrado na carreira.",
      valueSemantics:
        "Keyword match no campo curso ('mestrado', 'master', 'msc' etc.); quando a duração está datada e é <1.5 ano, não dispara mesmo com o rótulo 'mestrado' (filtra programas curtos disfarçados de mestrado).",
      correlatedWith: ["best_grad_tier"],
    },
  },
  has_competitive_course: {
    label: "Cursou área competitiva",
    description: "Cursou engenharia, medicina, computação, economia ou similar.",
    measurement: "keyword",
    group: "academic",
    scoring: {
      licenses:
        "A pessoa cursou uma área classicamente competitiva (engenharia, medicina, ciência da computação, economia ou correlata) — estabelece o TIPO de curso, não o desempenho nele.",
      doesNotImply:
        "O nome do curso não estabelece o rigor da instituição específica nem o desempenho da pessoa nele.",
      valueSemantics:
        "Detectado por palavra-chave no campo curso/área de qualquer entrada educacional (graduação ou pós); false quando nenhuma entrada corresponde às palavras-chave — não distingue 'área comprovadamente não competitiva' de 'área não capturada pela lista de palavras-chave'.",
      correlatedWith: ["course_x_institution_tier", "best_uni_tier"],
    },
  },

  // priors
  career_years: {
    label: "Anos de carreira",
    description:
      "Anos totais de carreira (primeiro start até último end). Limita o teto de senioridade.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece a extensão temporal total da carreira, do primeiro início ao encerramento mais recente — contexto de quanto tempo de mercado a pessoa acumulou.",
      doesNotImply:
        "Mais anos de carreira não estabelece progressão nem aprofundamento — uma carreira longa pode ter estagnado; corrobore com sinais de trajetória de rank e promoção.",
      valueSemantics:
        "É o intervalo calendário entre o primeiro início e o último fim registrados — inclui eventuais gaps sem vínculo formal no meio da carreira, não é soma de tempo efetivamente empregado.",
    },
  },
  max_anchor_years: {
    label: "Anos no empregador-âncora",
    description: "Tempo no empregador com maior tenure cumulativa.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece a duração no empregador de maior permanência cumulativa da carreira — contexto sobre o vínculo mais longo/estável, usado como âncora para avaliar profundidade potencial de atuação.",
      doesNotImply:
        "Tempo longo no empregador-âncora não estabelece, por si, profundidade de escopo alcançada ali — corrobore com sinais de progressão de título e responsabilidade dentro desse vínculo.",
      valueSemantics:
        "Calculado por união de intervalos — cargos concorrentes no mesmo empregador não inflam o valor.",
      correlatedWith: ["second_anchor_years", "avg_tenure_years", "has_lock_in_pattern"],
    },
  },
  second_anchor_years: {
    label: "Anos no segundo empregador-âncora",
    description: "Tempo no segundo empregador com maior tenure.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Quando não-nulo, estabelece a duração no segundo empregador de maior permanência cumulativa — contexto complementar ao anchor principal, indicando se há um segundo vínculo relevante de longo prazo.",
      doesNotImply:
        "Não estabelece continuidade ou proximidade temporal entre os dois vínculos-âncora — corrobore com a linha do tempo completa antes de inferir um padrão de dois pilares consecutivos.",
      valueSemantics:
        "null significa que a carreira tem apenas um empregador (não há segundo âncora a medir) — não é ausência de dado, é a carreira ter menos vínculos distintos.",
      correlatedWith: ["max_anchor_years"],
    },
  },
  num_employers_with_promotion: {
    label: "Empregadores com promoção (strict)",
    description: "Empregadores distintos onde houve ao menos uma promoção strict.",
    measurement: "numeric",
    group: "priors",
  },
  num_employers_with_loose_promotion: {
    label: "Empregadores com promoção (loose)",
    description: "Empregadores distintos onde houve ao menos uma promoção loose.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece em quantos empregadores distintos houve ao menos uma promoção (julgamento loose) — contexto sobre a disseminação do reconhecimento de progressão entre vínculos, não a intensidade de cada um.",
      doesNotImply:
        "Não estabelece a magnitude de cada promoção — corrobore com num_loose_promotions e a evidência de escopo por trás de cada uma antes de tratar como múltiplos saltos relevantes de nível.",
      valueSemantics:
        "Depende do julgamento 'loose' de promoção (classificador LLM mais permissivo, que prefere marcar promoção em caso de dúvida) — não é o mesmo classificador 'strict' usado em outros produtos.",
      correlatedWith: [
        "has_double_loose_promotion",
        "num_loose_promotions",
        "avg_loose_promo_interval_years",
        "promotion_cadence_regularity",
      ],
    },
  },
  has_double_promotion: {
    label: "Promoção dupla (strict)",
    description: "Duas promoções strict no mesmo empregador em <14 meses.",
    measurement: "numeric",
    group: "priors",
  },
  has_double_loose_promotion: {
    label: "Promoção dupla (loose)",
    description: "Duas promoções loose no mesmo empregador em <14 meses.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Um true estabelece duas promoções (julgamento loose) no mesmo empregador em menos de 14 meses — evidência de reconhecimento de desempenho em ritmo rápido naquele ciclo específico.",
      doesNotImply:
        "Não estabelece que o ritmo se repete ao longo da carreira — é um evento pontual localizado num empregador, não um padrão sustentado de aceleração.",
      valueSemantics:
        "Depende do julgamento 'loose' de promoção (classificador LLM mais permissivo que o 'strict', que prefere marcar promoção em caso de dúvida).",
      correlatedWith: [
        "num_employers_with_loose_promotion",
        "num_loose_promotions",
        "avg_loose_promo_interval_years",
        "promotion_cadence_regularity",
      ],
    },
  },
  has_lock_in_pattern: {
    label: "Padrão de lock-in",
    description: "≥10 anos no empregador-âncora SEM nenhuma promoção. Cap negativo na senioridade.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Um true estabelece dez anos ou mais no empregador de maior permanência sem qualquer mudança de título registrada nesse vínculo — contexto de possível estabilidade sem progressão visível de cargo.",
      doesNotImply:
        "Não estabelece ausência de crescimento real — mudança de escopo sem mudança formal de título (comum em estruturas enxutas) não é capturada; corrobore com evidência direta de responsabilidade antes de tratar como teto.",
      valueSemantics:
        "A ausência de promoção é uma proxy por contagem de títulos distintos nesse empregador (não pelo julgamento LLM de promoção) — pode subestimar mudanças reais de escopo sem troca formal de cargo.",
      correlatedWith: ["max_anchor_years", "max_anchor_promotion_count"],
    },
  },

  // seniority ladder — ordinal title-rank signals (level-assessment palette
  // expansion, 2026-06-09). Coarse 0–8 ladder via keyword heuristic.
  peak_title_rank: {
    label: "Rank do cargo mais alto atingido",
    description:
      "Senioridade ordinal (0–8) do título mais alto já ocupado: 0 entry → 2 sênior IC → 4 gerente → 6 diretor → 7 VP → 8 C-level/fundador.",
    measurement: "keyword",
    group: "progression",
    scoring: {
      licenses:
        "Estabelece a senioridade ordinal (0–8) do título mais alto já ocupado na carreira — o teto de rank de título já demonstrado, independentemente do cargo atual.",
      doesNotImply:
        "Um título de pico não estabelece que a responsabilidade real acompanhou o título, nem que o nível se sustentou — corrobore com evidência de atuação efetiva e com years_since_peak_title para saber se o pico é recente ou distante.",
      valueSemantics:
        "Ladder heurística por palavra-chave (0 entry → 2 IC sênior → 4 gerente → 6 diretor → 7 VP → 8 C-level/fundador); trilhas de consultoria/finanças usam uma sub-ladder própria para não sub-contar essas carreiras.",
      correlatedWith: ["latest_title_rank", "title_rank_velocity", "years_since_peak_title"],
    },
  },
  latest_title_rank: {
    label: "Rank do cargo mais recente",
    description:
      "Senioridade ordinal (0–8) do cargo atual/mais recente. Comparado ao pico, revela step-downs ou estabilidade no topo.",
    measurement: "keyword",
    group: "progression",
    scoring: {
      licenses:
        "Estabelece a senioridade ordinal (0–8, ladder de título) do cargo atual ou mais recente — situa onde a pessoa está agora na escada de título.",
      doesNotImply:
        "Um rank de título não estabelece o escopo real de atuação — corrobore com evidência de responsabilidade efetiva antes de tratar o título isoladamente como prova de nível.",
      valueSemantics:
        "Ladder heurística por palavra-chave (0 entry, 2 IC sênior, 4 gerente, 6 diretor, 7 VP, 8 C-level/fundador); comparado a peak_title_rank revela se a pessoa está abaixo do próprio teto ou ainda no ápice.",
      correlatedWith: ["peak_title_rank", "title_rank_velocity", "years_since_peak_title"],
    },
  },
  title_rank_velocity: {
    label: "Velocidade de ascensão (ranks/ano)",
    description:
      "Ranks de senioridade ganhos por ano de carreira ((pico − primeiro cargo) ÷ anos de carreira). Maior = ascensão mais rápida.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Estabelece a velocidade média de ascensão de rank de título ao longo da carreira (pico menos primeiro cargo, dividido pelos anos de carreira) — valor maior evidencia ascensão mais rápida.",
      doesNotImply:
        "Não estabelece patamar absoluto de senioridade — velocidade alta partindo de um piso baixo pode ainda não ter alcançado responsabilidade sênior; corrobore com peak_title_rank para o nível absoluto.",
      valueSemantics:
        "Divide pelo total de anos de carreira (piso de ~1 ano para não inflar carreiras curtas); carreiras muito curtas com um único salto de rank podem gerar velocidades altas com baixa robustez estatística.",
      correlatedWith: ["peak_title_rank", "latest_title_rank", "years_since_peak_title"],
    },
  },
  years_since_peak_title: {
    label: "Anos desde o cargo de pico",
    description:
      "Anos desde a última ocorrência do cargo mais sênior. 0 = ainda no pico; alto = pode ter regredido de nível.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Um valor baixo (perto de 0) estabelece que a pessoa está atualmente no rank de título mais alto que já ocupou — ainda no próprio teto.",
      doesNotImply:
        "Um valor alto não estabelece regressão de nível por si só — pode refletir mudança de carreira, pausa ou reposicionamento voluntário; corrobore com latest_title_rank e o contexto da transição antes de tratar como sinal negativo.",
      valueSemantics:
        "0 significa que o cargo de pico é o atual ou o mais recente; calculado a partir da última ocorrência do rank de pico — se a pessoa retomou esse rank após um período em rank menor, a contagem reinicia a partir dessa última ocorrência.",
      correlatedWith: ["peak_title_rank", "latest_title_rank", "title_rank_velocity"],
    },
  },

  // career shape — deterministic stability/breadth signals (palette
  // expansion, 2026-06-09).
  num_employers: {
    label: "Número de empregadores",
    description:
      "Empregadores distintos na carreira (dedupe por nome). Proxy de estabilidade vs. job-hopping.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece o número de empregadores distintos ao longo da carreira — contexto de estabilidade vs. rotatividade, não uma medida de qualidade da atuação em nenhum deles.",
      doesNotImply:
        "Poucos empregadores não estabelece lock-in, e muitos não estabelece falta de comprometimento — o número isolado é neutro; corrobore com tenure_shape e evidência de progressão.",
      valueSemantics:
        "Dedupe por nome de empregador normalizado (minúsculas/trim) — variações de grafia do mesmo empregador podem, por ruído de dado, ser contadas como empregadores distintos.",
      correlatedWith: ["avg_tenure_years", "tenure_shape"],
    },
  },
  avg_tenure_years: {
    label: "Tenure médio por empregador",
    description:
      "Média de anos por empregador (união de intervalos — não conta em dobro cargos concorrentes na mesma empresa).",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece a duração média de permanência por empregador (união de intervalos, sem inflar por cargos concorrentes) — contexto sobre o ritmo típico de vínculo ao longo da carreira, não uma medida direta de profundidade de atuação.",
      doesNotImply:
        "Um tenure médio alto não estabelece profundidade de escopo ou impacto em nenhum vínculo — corrobore com evidência de responsabilidade e entregas antes de tratar como sinal de senioridade.",
      valueSemantics:
        "Calculado por união de meses por empregador — cargos concorrentes no mesmo empregador não duplicam a contagem; carreiras com poucos empregadores tornam a média sensível a um único vínculo atípico.",
      correlatedWith: ["tenure_shape", "num_employers", "max_anchor_years"],
    },
  },
  num_distinct_functions: {
    label: "Funções distintas exercidas",
    description:
      "Quantos domínios funcionais distintos (eng, produto, vendas, RH, finanças, etc.) ao longo da carreira. Proxy de amplitude.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece a amplitude de domínios funcionais distintos (engenharia, produto, vendas, RH, finanças etc.) exercidos ao longo da carreira — contexto de versatilidade funcional, não de profundidade em nenhum deles.",
      doesNotImply:
        "Maior número de funções distintas não estabelece maior senioridade nem generalismo estratégico — pode refletir mudanças de rumo sem aprofundamento; corrobore com evidência de profundidade em cada função.",
      valueSemantics:
        "Classificação por bucket funcional via palavras-chave do título — títulos ambíguos ou fora do vocabulário mapeado caem em 'other' e não contam; contagem baixa em carreiras curtas é esperada, não evidência de rigidez.",
      correlatedWith: ["num_within_employer_function_switches"],
    },
  },
  years_since_last_management_role: {
    label: "Anos desde último cargo de gestão",
    description:
      "Anos desde o cargo de gestão (rank ≥ gerente) mais recente. Nulo se nunca geriu. Mede recência da experiência de liderança.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Quando não-nulo, estabelece a recência da experiência de gestão de pessoas mais recente da carreira — contexto sobre se a liderança é atual ou distante no tempo.",
      doesNotImply:
        "Um valor baixo (experiência recente) não estabelece, por si, profundidade ou escopo de gestão — mede apenas recência; corrobore com sinais de profundidade de gestão para avaliar o nível da experiência.",
      valueSemantics:
        "null significa que a pessoa nunca ocupou cargo classificado como gestão de pessoas (rank ≥ gerente na régua de título) — não indica ausência de dado, indica ausência do padrão. O rank é inferido por palavra-chave do título e pode subestimar gestão informal sem título de 'manager/gerente'.",
      correlatedWith: ["peak_title_rank", "latest_title_rank"],
    },
  },

  // scope magnitude — LLM scope-depth signals (palette expansion,
  // 2026-06-09). Estimam o TAMANHO do que a pessoa geriu/construiu.
  managed_team_size_bucket: {
    label: "Tamanho do maior time gerido (faixa)",
    description:
      "Maior time sob responsabilidade (diretos + indiretos): none / 1-5 / 6-20 / 21-100 / 100+. Estimado por LLM.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Estabelece o tamanho do MAIOR time/organização (diretos + indiretos) sob responsabilidade da pessoa em algum ponto da carreira — por headcount declarado ou por um título de gestão inequívoco combinado ao porte da empresa.",
      doesNotImply:
        "Um título de gestão sem headcount declarado licencia apenas a MENOR faixa consistente com título + porte da empresa, nunca a maior plausível; título de 'manager' IC (Product/Project/Account Manager) não eleva a faixa acima de 'none' sem evidência adicional de reports reais.",
      valueSemantics:
        "Enum: none / 1-5 / 6-20 / 21-100 / 100+. 'none' é ausência EVIDENCIADA (trilha IC-only, ponta a ponta, sem título de gestão jamais) — não é o default para 'não mencionado'. null = desconhecido (perfil fragmentário demais para classificar a trilha, ex.: 'Sócio' numa boutique minúscula).",
      correlatedWith: ["has_managed_managers", "has_scaled_team_or_function"],
    },
  },
  has_managed_managers: {
    label: "Geriu outros gestores (skip-level)",
    description:
      "Liderou uma organização de times (gestores reportando a gestores), não apenas um time único. Discriminador L5+.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Evidencia liderança de uma organização de times — gestores reportando a outros gestores (skip-level) — via descrição explícita de reports gerenciais ou um título Diretor+/C-level/VP sobre função inequivocamente multi-time em empresa grande o bastante para exigir camadas de gestão. É o discriminador estrutural mais alto de span de gestão do catálogo.",
      doesNotImply:
        "Um título sênior isolado ou uma empresa grande não bastam — sem evidência explícita de que gestores reportam a gestores, não presuma skip-level a partir de senioridade percebida ou porte do empregador.",
      valueSemantics:
        "Sinal LLM três-estados: null = desconhecido (nunca evidência de ausência); false só quando a estrutura mostra positivamente um gestor de primeira linha com reports IC numa função/empresa pequena — ambiguidade (ex.: 'Director of Operations' numa startup de 51-200 sem descrição) resolve para null, não false.",
      correlatedWith: ["managed_team_size_bucket"],
    },
  },
  has_scaled_team_or_function: {
    label: "Construiu / escalou time ou função",
    description:
      "Montou time/função do zero (0→1) ou escalou via crescimento real (ex.: 'cresci o time de 3 para 40', 'estruturei a área de comp').",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Evidencia que a pessoa construiu um time/função a partir de quase-zero (0→1) ou escalou um existente via crescimento real e narrado (ex.: 'cresci o time de 3 para 40', 'estruturei a área de CS do zero'). Exige texto — não é inferível só de título.",
      doesNotImply:
        "Um título como 'Founding AE' ou 'Head of X' numa startup, sozinho, sugere mas não comprova construção — sem texto descrevendo o crescimento ou a construção, o sinal fica em aberto, não confirmado.",
      valueSemantics:
        "Description-gated: true exige evidência textual explícita; null é o default para perfis finos (ex.: 'Head of CS' numa startup sem descrição — plausível, mas não mostrado); false só quando a estrutura mostra positivamente um padrão caretaker (todos os cargos entraram em funções já maduras e as descrições narram operar, não construir) — raro, prefira null.",
      correlatedWith: ["managed_team_size_bucket"],
    },
  },
  peak_org_size_bucket: {
    label: "Tamanho da maior org em cargo sênior (faixa)",
    description:
      "Porte da maior organização onde ocupou cargo sênior: <50 / 50-200 / 201-1000 / 1000+. Mede o tamanho do palco.",
    measurement: "llm",
    group: "environment",
    scoring: {
      licenses:
        "Um valor estabelece o porte (faixa de headcount) da MAIOR organização onde a pessoa ocupou um cargo sênior (gerente-ou-acima, ou IC sênior de influência clara) — mede o tamanho do palco, não a atuação da pessoa nele.",
      doesNotImply:
        "Um bucket alto não estabelece senioridade do cargo em si nem tempo de permanência — um cargo júnior breve na mesma empresa grande nunca licencia o bucket; só o cargo efetivamente sênior é lido.",
      valueSemantics:
        "Preferencialmente lido de staffCountRange do empregador no cargo sênior; na ausência desse dado, pode vir de conhecimento de marca de empregadores bem conhecidos — não é a mesma evidência do classificador de tier (Tier-1/Tier-2), que julga bar de contratação, não headcount.",
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // Palette expansion batch 2 (2026-06-09). Untapped profile sections,
  // quantified impact, structural-flag negatives, trajectory shape, domain
  // depth, education interaction. NOTE: the comp-universal seed matrix now
  // routes several of these (course_x_institution_tier, best_grad_tier);
  // most credentials/recognition/geography entries remain unrouted there.
  // ───────────────────────────────────────────────────────────────────────

  // credentials / idiomas
  num_languages: {
    label: "Número de idiomas",
    description: "Quantos idiomas a pessoa lista no perfil.",
    measurement: "numeric",
    group: "credentials",
  },
  has_professional_multilingual: {
    label: "Multilíngue profissional",
    description:
      "≥2 idiomas em proficiência profissional ou nativa. Sinal fraco — viés de credencial.",
    measurement: "keyword",
    group: "credentials",
  },
  has_domain_certification: {
    label: "Certificação de domínio",
    description:
      "Possui certificação reconhecida (CFA/PMP/AWS/CISSP/etc.). Valida conteúdo, não desempenho.",
    measurement: "keyword",
    group: "credentials",
  },
  certification_count: {
    label: "Número de certificações",
    description: "Quantidade de certificações listadas.",
    measurement: "numeric",
    group: "credentials",
  },
  has_continuing_education: {
    label: "Educação continuada",
    description:
      "Cursou programa curto / certificado / micromasters / educação executiva (não diploma pleno).",
    measurement: "keyword",
    group: "credentials",
  },

  // reconhecimento / produção intelectual
  has_publications: {
    label: "Tem publicações",
    description:
      "Possui publicações listadas. Forte em pesquisa/academia, fraco em papéis operacionais.",
    measurement: "numeric",
    group: "recognition",
    scoring: {
      licenses:
        "True estabelece que o candidato lista ao menos uma publicação no perfil — forte em trajetórias de pesquisa/academia, mas também aparece em papéis técnicos/científicos aplicados.",
      doesNotImply:
        "Não estabelece o tipo, veículo ou relevância da publicação (paper revisado por pares vs. post interno) — corrobore com o contexto de research/pós-graduação antes de tratar como prova de profundidade acadêmica.",
      valueSemantics:
        "Depende do candidato ter preenchido a seção 'Publications' do LinkedIn, pouco usada mesmo por quem publica — false aqui é um sinal fraco de ausência real.",
    },
  },
  publication_count: {
    label: "Número de publicações",
    description: "Quantidade de publicações listadas.",
    measurement: "numeric",
    group: "recognition",
  },
  has_patents: {
    label: "Tem patentes",
    description: "Possui patentes listadas. Sinal de deep-tech / P&D.",
    measurement: "numeric",
    group: "recognition",
    scoring: {
      licenses:
        "True estabelece que o candidato lista ao menos uma patente no perfil — sinal de atuação em P&D / deep-tech onde invenção patenteável faz parte do trabalho.",
      doesNotImply:
        "Não estabelece o papel do candidato na invenção (inventor principal vs. colaborador listado) nem substitui evidência direta de profundidade técnica — corrobore com sinais de profundidade técnica/IC antes de tratar como prova de nível.",
      valueSemantics:
        "Depende inteiramente do candidato ter preenchido a seção 'Patents' do LinkedIn, pouco usada mesmo por inventores reais — false aqui é um sinal fraco de ausência genuína.",
    },
  },
  has_notable_award: {
    label: "Tem prêmios / honrarias",
    description:
      "Possui prêmios ou honrarias listados (prestígio avaliado por award_prestige_tier).",
    measurement: "numeric",
    group: "recognition",
  },
  has_side_projects: {
    label: "Tem projetos próprios",
    description: "Lista projetos / side-projects. Sinal de iniciativa / builder.",
    measurement: "numeric",
    group: "recognition",
  },
  award_prestige_tier: {
    label: "Prestígio do maior prêmio (tier)",
    description:
      "0 negligível → 1 empresa/regional → 2 indústria/nacional → 3 mundial. Nulo se sem prêmios.",
    measurement: "llm",
    group: "recognition",
    scoring: {
      licenses:
        "Um tier presente (0–3) estabelece o prestígio do prêmio/honraria mais notável do candidato, julgado pelo LLM a partir do texto listado — 3 é reconhecimento mundial, 2 é reconhecimento de indústria/nacional, 1 é reconhecimento de empresa/regional/acadêmico-interno, 0 é prêmio presente porém de prestígio negligível.",
      doesNotImply:
        "O tier avalia apenas o prêmio mais notável listado, não a carreira como um todo — não substitui evidência de atuação profissional; use como corroboração, não como driver primário.",
      valueSemantics:
        "Nulo quando não há prêmios listados (abstenção, nunca evidência de ausência de mérito) — distinto de tier 0, que significa prêmio presente mas de prestígio negligível; a escala é deliberadamente conservadora (o prompt instrui escolher o bucket menor quando em dúvida).",
    },
  },

  // alcance geográfico
  num_countries_worked: {
    label: "Países onde trabalhou",
    description:
      "Países distintos inferidos das localizações dos cargos (+ país de origem). Heurística grosseira.",
    measurement: "numeric",
    group: "geography",
  },
  has_international_experience: {
    label: "Experiência internacional",
    description: "Trabalhou em ≥2 países.",
    measurement: "numeric",
    group: "geography",
    scoring: {
      licenses:
        "A pessoa trabalhou em 2 ou mais países distintos (localizações de cargo mais país de origem do perfil) — estabelece exposição geográfica multi-país no histórico de carreira.",
      doesNotImply:
        "Não estabelece profundidade de atuação internacional (expatriação, gestão cross-border, mobilidade formal) — é uma contagem geográfica bruta que já conta o país de origem como um dos dois países.",
      valueSemantics:
        "Derivado de heurística grosseira (último segmento de cada string de localização de cargo, mais o país declarado do perfil); formatação de localização inconsistente torna subcontagem mais provável que superconcontagem — false/ausente não deve ser lido como 'carreira comprovadamente doméstica'.",
    },
  },

  // impacto quantificado
  has_commercial_ownership: {
    label: "Ownership comercial",
    description:
      "Foi dono de resultado comercial — receita/quota/retenção, P&L de unidade/categoria, key account ou investimento independente. Gate canônico de L4 (pedigree sozinho não basta).",
    measurement: "llm",
    group: "impact",
    scoring: {
      licenses:
        // biome-ignore lint/security/noSecrets: pt-BR scoring semantics, not a credential
        "Um true estabelece ownership de um resultado comercial — receita/quota/retenção de uma carteira real, P&L de unidade/categoria, uma conta-chave, ou ownership de investimento (sourcing→decisão) — via descrição explícita ou via o prior estrutural (trilha multi-ano ≥2 anos em título quota-carrying como Account Executive/Sales Manager/Country Manager em firma séria).",
      doesNotImply:
        "O prior estrutural licencia apenas ownership do número, nunca a magnitude do resultado — papéis de suporte/analítico que só informam decisões comerciais (sales ops, pré-vendas técnico) não contam mesmo quando adjacentes ao tema.",
      valueSemantics:
        "Tri-state: null = desconhecido (trilhas de consultoria/estratégia ou founder híbrido sem descrição podem ou não incluir ownership — nunca leia como ausência); false só quando toda a carreira é visivelmente não-comercial ponta a ponta (ex.: back-office interno o tempo todo).",
    },
  },
  revenue_scaled_bucket: {
    label: "Receita escalada (faixa)",
    description:
      "Maior receita que as descrições atribuem ao ownership da pessoa: none / <1M / 1-10M / 10-100M / 100M+.",
    measurement: "llm",
    group: "impact",
  },
  capital_raised_bucket: {
    label: "Capital captado/alocado (faixa)",
    description:
      "Capital captado (founder/exec) ou alocado (investidor): none / <1M / 1-10M / 10-50M / 50M+.",
    measurement: "llm",
    group: "impact",
  },
  headcount_grown_bucket: {
    label: "Time construído/escalado (faixa)",
    description:
      "Maior time que a pessoa CONSTRUIU/cresceu (não apenas geriu): none / to-10 / to-50 / 50+.",
    measurement: "llm",
    group: "impact",
  },

  // sinais estruturais / caps (negativos)
  is_passive_finance_career: {
    label: "Carreira de finanças passiva",
    description:
      "Dominada por finanças passiva (equity research / VC-PE passivo), sem advisory/M&A/comercial. Cap ~L3.",
    measurement: "llm",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece que a carreira é dominada por finanças passiva (equity research, VC/PE passivo, gestão de ativos) sem nenhuma evidência de advisory a cliente, execução de M&A, consultoria ou ownership comercial — um padrão que argumenta contra o topo da faixa em eixos que dependem de execução/ownership.",
      doesNotImply:
        "Não é avaliação de competência técnica em finanças — mede ausência de um tipo específico de exposição, não a qualidade da análise produzida; pondere com evidência de profundidade analítica antes de tratar como teto definitivo.",
    },
  },
  is_admin_comp_payroll_dominant: {
    label: "Admin de comp/folha dominante",
    description:
      "Função recente dominante é a administração de comp/benefícios/folha (operação), não desenho/estratégia.",
    measurement: "llm",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece que a função dominante RECENTE da carreira é a administração operacional de compensação/benefícios/folha (processamento), não o desenho ou a estratégia dessas áreas — um padrão que argumenta contra o topo da faixa em eixos de profundidade estratégica.",
      doesNotImply:
        "Não é prova isolada de teto baixo — administração operacional pode coexistir com responsabilidades estratégicas não capturadas nesse recorte; pondere com outra evidência de profundidade antes de tratar como determinante.",
      correlatedWith: ["is_all_internal_hr_at_non_hr_companies"],
    },
  },
  is_all_internal_hr_at_non_hr_companies: {
    label: "RH interno a vida toda",
    description:
      "Carreira inteira em RH interno de empresas não-RH — nunca atuou em employer de serviços/tech de RH.",
    measurement: "llm",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece que toda a carreira documentada foi em RH interno de empresas cujo negócio não é RH, sem passagem por empregador de serviços/tech/consultoria de RH — um padrão que argumenta contra o benchmarking de mercado por pares do setor.",
      doesNotImply:
        "Não estabelece menor competência técnica em RH — apenas que a experiência não foi validada num contexto de mercado de RH-as-a-business; pondere com profundidade de atuação antes de tratar como teto de especialização.",
      correlatedWith: ["is_admin_comp_payroll_dominant"],
    },
  },
  moved_away_from_client_facing: {
    label: "Afastou-se de client-facing",
    description:
      "Trajetória que saiu de trabalho client-facing (ex.: consultoria → estratégia interna); cargos recentes internos.",
    measurement: "llm",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece uma trajetória que se afastou de trabalho client-facing (ex.: consultoria → estratégia interna), com os cargos mais recentes sendo exclusivamente internos — um padrão que argumenta contra eixos que dependem de exposição atual a cliente.",
      doesNotImply:
        "Não estabelece regressão de senioridade — migrar de client-facing para atuação interna costuma ser lateral ou até uma progressão; pondere com o eixo relevante antes de tratar como teto geral.",
      correlatedWith: ["sm_years_client_facing", "sm_num_client_facing_roles"],
    },
  },
  single_low_tier_employer_whole_career: {
    label: "Empregador único não-elite",
    description:
      "Carreira inteira (pós-estágio) num único employer fora de Tier-1/Tier-2 — benchmarking externo limitado.",
    measurement: "llm",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece que, descontando estágios, toda a carreira foi num único empregador fora de Tier-1/Tier-2 — um padrão que argumenta contra a confiabilidade do benchmarking externo, por faltar ponto de comparação de mercado em outro empregador.",
      doesNotImply:
        "Não estabelece menor competência ou escopo — apenas que falta comparação externa para calibrar o nível; pondere com a evidência de escopo e progressão dentro desse único empregador antes de tratar como teto.",
      valueSemantics:
        "O julgamento de tier do empregador (Tier-1/Tier-2 vs. não) é feito pelo mesmo LLM, na mesma chamada — não é um lookup determinístico de uma lista fixa de empresas.",
    },
  },
  stagnation_over_5y_same_level: {
    label: "Estagnação >5 anos no mesmo nível",
    description:
      "Mais de 5 anos desde a última subida de rank de título. Dispara em qualquer tenure (≠ lock-in).",
    measurement: "numeric",
    group: "flags",
    scoring: {
      licenses:
        "Um true estabelece que se passaram mais de 5 anos desde a última subida de rank de título — um padrão que argumenta contra a hipótese de expansão recente de escopo; dispara em qualquer tenure, diferente do lock-in (que exige 10 anos num único empregador).",
      doesNotImply:
        "Não estabelece teto de nível nem estagnação de competência — a régua de título pode não capturar expansão de escopo sem mudança formal de cargo; pondere com evidência direta de responsabilidade atual antes de tratar como teto.",
      valueSemantics:
        "Calculado a partir da última subida de rank de título detectada pela régua textual (não LLM) — se a pessoa nunca subiu de rank, o relógio conta desde o início da carreira, o que pode marcar como estagnada uma carreira curta com um único cargo.",
      correlatedWith: ["promotion_cadence_regularity", "years_since_peak_title"],
    },
  },

  // trajetória — refinamentos
  has_deep_rooting: {
    label: "Enraizamento profundo (positivo)",
    description:
      "≥10 anos no mesmo employer COM mudança de título. Gêmeo positivo do lock-in (lealdade + crescimento).",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "True estabelece ≥10 anos no mesmo empregador COM ao menos uma mudança de título nesse período — permanência longa acompanhada de evidência de crescimento, não apenas estabilidade estática.",
      doesNotImply:
        "Não estabelece a magnitude do crescimento — a mudança de título pode ter sido um único degrau ou lateral; corrobore com o rank de título ou a cadência de promoção para calibrar o tamanho do avanço.",
      correlatedWith: ["has_lock_in_pattern", "tenure_shape"],
    },
  },
  tenure_shape: {
    label: "Formato dos vínculos",
    description:
      "Distribuição das durações por employer: sticky (longos) / mixed / rotating (curtos). Mesmo avg, narrativa diferente.",
    measurement: "numeric",
    group: "priors",
    scoring: {
      licenses:
        "Estabelece o formato da distribuição de permanência entre empregadores (predominância de vínculos longos, curtos ou mistos) — contexto complementar ao tenure médio, capturando a forma da distribuição e não apenas seu centro.",
      doesNotImply:
        "'rotating' não estabelece falta de comprometimento nem 'sticky' estabelece falta de ambição — o formato descreve o padrão de vínculos, não a qualidade da atuação em cada um.",
      valueSemantics:
        "sticky = ≥50% dos empregadores com ≥4 anos; rotating = ≥60% dos empregadores com <2 anos; mixed = nenhum limiar atingido. Classificação por limiares fixos sobre a distribuição, não uma média.",
      correlatedWith: ["avg_tenure_years", "num_employers"],
    },
  },
  promotion_cadence_regularity: {
    label: "Regularidade da cadência de promoção",
    description:
      "Uniformidade dos intervalos entre subidas de rank (0–1, maior = mais regular). Proxy determinístico via título.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Um valor alto (perto de 1) estabelece que as subidas de rank de título ao longo da carreira ocorreram em intervalos regulares — trajetória com cadência previsível, não picos isolados.",
      doesNotImply:
        "Não estabelece a velocidade nem o tamanho dos degraus — mede só a uniformidade do espaçamento entre subidas; uma cadência regular pode ser regularmente lenta, corrobore com title_rank_velocity para a velocidade real.",
      valueSemantics:
        "Nulo quando há menos de 2 subidas de rank detectadas (carreira sem múltiplos degraus, não evidência de estagnação); usa a ladder determinística de título em toda a carreira, não o classificador loose — pode divergir de num_loose_promotions quando a descrição evidencia promoções que o título não capta.",
      correlatedWith: [
        "num_loose_promotions",
        "avg_loose_promo_interval_years",
        "num_employers_with_loose_promotion",
        "has_double_loose_promotion",
      ],
    },
  },
  num_within_employer_function_switches: {
    label: "Trocas de função dentro do employer",
    description:
      "Mudanças de domínio funcional dentro de um mesmo employer. Proxy de versatilidade / confiança organizacional.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Cada troca estabelece que a pessoa mudou de domínio funcional (ex.: engenharia → produto) dentro do mesmo empregador — evidência de versatilidade e de confiança organizacional para migrar de função sem trocar de empresa.",
      doesNotImply:
        "Não estabelece profundidade em nenhuma das funções — pode refletir amplitude rasa tanto quanto versatilidade real; corrobore com anos por função para calibrar profundidade.",
      valueSemantics:
        "Contagem bruta por domínio funcional detectado via palavra-chave de título, que pode sub ou super-contar quando títulos são ambíguos ou genéricos.",
      correlatedWith: ["num_ic_track_advancements"],
    },
  },
  num_growth_window_entries: {
    label: "Entradas em janela de crescimento",
    description:
      "Quantas empresas a pessoa entrou durante a janela de hipercrescimento delas. Estimado por LLM.",
    measurement: "llm",
    group: "progression",
    scoring: {
      licenses:
        "Cada empresa contada estabelece que a pessoa entrou durante a janela de hipercrescimento daquele empregador (ex.: startup pré-Series-B, ou o período de hypergrowth de uma empresa hoje grande) — evidência de exposição a ambiente de escala rápida.",
      doesNotImply:
        "Não estabelece que a pessoa provocou o crescimento ou teve papel de liderança nele — apenas que esteve presente durante a janela; corrobore com sinais de ownership comercial ou de time construído para saber se o papel foi ativo.",
      valueSemantics:
        "0 é o valor default tanto quando nenhuma janela de hipercrescimento foi identificada quanto quando a extração LLM falhou — trate como 'não estabelecido', nunca como evidência de carreira em empresas estáveis.",
    },
  },
  has_exit_or_ma_experience: {
    label: "Experiência de exit / M&A",
    description:
      "Fundou empresa adquirida, conduziu integração de aquisição ou vendeu unidade de negócio.",
    measurement: "llm",
    group: "founder",
    scoring: {
      licenses:
        "Evidência textual explícita de que a pessoa fundou empresa adquirida, liderou/operou uma integração de aquisição, ou vendeu uma unidade de negócio — estabelece experiência direta com o ciclo de uma transação, não cobertura passiva.",
      doesNotImply:
        "Não estabelece o porte ou a relevância da transação — apenas que a pessoa esteve no papel descrito no episódio de exit/M&A.",
      valueSemantics:
        "null = desconhecido (nunca evidência de ausência); o extrator só marca false quando a estrutura da carreira descarta a possibilidade (carreira curta, júnior, sem papel executivo ou fundador em nenhum ponto) — um founder cujo desfecho da empresa não é mencionado permanece null, não false.",
    },
  },

  // domínio / amplitude
  years_in_primary_industry: {
    label: "Anos na indústria principal",
    description:
      "Anos (união) na vertical mais focada, via companyIndustry. Proxy de profundidade de domínio. ⚠ Atualmente sempre NA — o actor do LinkedIn não fornece companyIndustry (não rotear até ser ingerido).",
    measurement: "numeric",
    group: "environment",
  },
  industry_focus_ratio: {
    label: "Concentração de indústria (0–1)",
    description:
      "Fração da carreira na indústria principal. Alto = especialista de domínio; baixo = generalista. ⚠ Atualmente sempre NA — o actor do LinkedIn não fornece companyIndustry (não rotear até ser ingerido).",
    measurement: "numeric",
    group: "environment",
  },
  vc_pe_investor_years: {
    label: "Anos em assento de investidor",
    description:
      "Tempo em cargo de VC/PE/investidor. Proxy de prontidão para founder / literacia de mercado.",
    measurement: "numeric",
    group: "founder",
  },
  staff_archetype: {
    label: "Arquétipo staff+ (Larson)",
    description:
      "tech_lead / architect / solver / right_hand para carreiras IC sênior; nulo para trilha de gestão ou júnior.",
    measurement: "llm",
    group: "management",
    scoring: {
      licenses:
        "Para carreiras de perfil sênior-IC (não gestão), identifica qual arquétipo Will-Larson melhor descreve o papel: tech_lead (guia a execução de um time), architect (dono da direção de um domínio/sistema por múltiplos anos), solver (é acionado para os problemas mais ambíguos) ou right_hand (extensão de um executivo, tipo chief-of-staff, com alavancagem organizacional ampla).",
      doesNotImply:
        "É uma leitura qualitativa da FORMA da trilha IC, não uma medida de senioridade ou impacto — não use para inferir nível hierárquico nem para comparar magnitude entre arquétipos diferentes, e carreiras de trilha de gestão ou perfis júnior não recebem arquétipo (isso não é evidência de ausência de senioridade).",
      valueSemantics:
        "Enum categórico: tech_lead / architect / solver / right_hand. null cobre tanto trilhas de gestão/júnior (não aplicável) quanto perfil sênior-IC cujo arquétipo específico não dá para determinar — os dois casos são tratados de forma idêntica a jusante, nunca infira qual dos dois se trata.",
    },
  },

  // interação acadêmica
  course_x_institution_tier: {
    label: "Curso × tier da instituição",
    description:
      "Interação entre competitividade do curso e tier da graduação (em vez de dois sinais separados). Derivado.",
    measurement: "numeric",
    group: "academic",
    scoring: {
      licenses:
        "Interação entre o tier da melhor graduação e a competitividade do curso — um valor alto estabelece seleção institucional relevante especificamente EM curso reconhecidamente competitivo (engenharia, medicina, computação, economia ou correlata).",
      doesNotImply:
        "Não estabelece desempenho durante o curso nem aplicação prática da formação — é uma combinação mecânica de dois proxies de seleção no ingresso.",
      valueSemantics:
        "Derivado = best_uni_tier × 1.0 quando o curso é competitivo, × 0.8 quando não é; herda o null de best_uni_tier quando a graduação não é classificável.",
      correlatedWith: ["best_uni_tier", "best_grad_tier", "has_competitive_course"],
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // IC / analytical depth (2026-06-09 imbalance fix). Strong evidence for
  // individual-contributor / consulting / finance tracks the management-
  // weighted palette misses. Backed by `llm-ic-depth.md`. NOTE: the
  // comp-universal seed matrix now routes these (top_consulting_track_level,
  // consulting_engagement_leadership, analytical_years, etc.).
  // ───────────────────────────────────────────────────────────────────────
  has_deep_analytical_ownership: {
    label: "Ownership analítico profundo",
    description:
      "Foi dono de análise / modelo / recomendação que moveu uma decisão real (não apenas produziu slides). Sinal IC forte.",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um true estabelece que a pessoa foi dona de uma análise, modelo, diligência ou recomendação específica que embasou uma decisão real e consequente de terceiros.",
      doesNotImply:
        "Não estabelece que o artefato passou a ser usado recorrentemente por outras pessoas (isso é has_technical_ic_artifact) nem qualquer porte de transação associada.",
      valueSemantics:
        "NO-PRIOR: nunca inferido de título, empresa ou tenure isoladamente. null = desconhecido (perfil fino, descrições vazias — nunca evidência de ausência); false exige evidência positiva de trabalho só de suporte/produção de material.",
    },
  },
  has_technical_ic_artifact: {
    label: "Artefato técnico IC reutilizado",
    description:
      "Construiu sistema / modelo / dataset / pesquisa do qual outras pessoas dependem, como IC sênior (não como gestor).",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um true estabelece que a pessoa construiu, como IC sênior (não como gestor delegando), um sistema, modelo, dataset, pesquisa ou ferramenta do qual outras pessoas passaram a depender.",
      doesNotImply:
        "Não estabelece ownership de uma decisão de negócio (isso é has_deep_analytical_ownership) nem que o artefato opera em escala de plataforma — apenas que terceiros dependem dele.",
      valueSemantics:
        "NO-PRIOR: nenhum título ('Data Scientist', 'Quant') licencia isso sozinho. null = desconhecido; false só quando a descrição mostra positivamente que o trabalho nunca produziu um artefato dependido por outros.",
    },
  },
  has_executed_transactions: {
    label: "Executou transações (M&A/PE)",
    description:
      "Executou transações de M&A / PE / financiamento ponta a ponta (não cobertura passiva). Distinto do flag de finanças passiva.",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um true estabelece execução de transações de M&A/PE/financiamento/mercado de capitais ponta a ponta (processo, modelagem, negociação, fechamento) — via descrição explícita ou via o prior estrutural de uma trilha de execução legível em título+firma+tenure (ex.: IB Associate→VP, PE Associate).",
      doesNotImply:
        "O prior estrutural por título nunca estabelece o porte do deal — isso fica com transaction_scale, e cobertura passiva (equity/credit research) não conta como execução mesmo quando adjacente ao tema.",
      valueSemantics:
        "Tri-state: null = desconhecido — inclusive quando um cargo poderia esconder trabalho de deal sem descrevê-lo (ex.: 'Consultant — Transaction Services' sem texto); false só quando a estrutura evidencia claramente uma trilha sem nenhum trabalho de transação.",
      correlatedWith: [
        "transaction_scale",
        "top_consulting_track_level",
        "ba_has_top_finance",
        "ba_years_in_finance",
      ],
    },
  },
  transaction_scale: {
    label: "Porte da maior transação",
    description: "Maior deal executado: none / <10M / 10-100M / 100M-1B / 1B+. Estimado por LLM.",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um valor não-null estabelece o porte do maior deal executado pela pessoa, quando um tamanho está explicitamente declarado ou diretamente computável do texto.",
      doesNotImply:
        "Um valor null não significa que a pessoa não executou transações — o prior estrutural por título de has_executed_transactions nunca preenche a escala sozinho.",
      valueSemantics:
        "'none' reservado para quando a carreira evidentemente não contém nenhum trabalho de transação; null é o default quando há execução mas nenhum tamanho é mencionado — não confundir os dois estados.",
      correlatedWith: ["has_executed_transactions"],
    },
  },
  consulting_engagement_leadership: {
    label: "Liderou engagement de consultoria",
    description:
      "Liderou workstream / engagement (case-team lead+), não apenas atuou como analista júnior no time. NÃO restrito a marcas — dispara para boutiques. Pode ser inferido da ESTRUTURA da carreira (trilha de consultoria ≥3 anos com progressão ou título sênior) mesmo sem descrições — a leitura que um humano faz. null = desconhecido (nunca evidência de ausência).",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um true estabelece que a pessoa liderou um workstream ou engajamento de consultoria — case lead, gerente de engajamento, ou dona de uma frente de cliente ponta a ponta — via descrição explícita ou via o prior estrutural (trilha de consultoria com tenure total ≥3 anos e progressão ou título sênior).",
      doesNotImply:
        "O prior estrutural licencia apenas liderança de workstream, nunca escala sozinho até tier de partner/sócio — esse julgamento é de top_consulting_track_level.",
      valueSemantics:
        "Tri-state: null = desconhecido (trilha júnior curta, sem progressão, sem descrição — nunca evidência de ausência); false exige que a descrição mostre positivamente trabalho de analista supervisionado sem progressão.",
      correlatedWith: ["top_consulting_track_level", "analytical_years"],
    },
  },
  top_consulting_track_level: {
    label: "Nível na trilha de consultoria/finanças",
    description:
      "Maior tier numa trilha de consultoria/finanças SÉRIA — marcas (MBB / Big-4 / IB bulge-bracket) E boutiques de calibre corroborado (porte, clientes, pedigree retro-validando): none / analyst / manager / partner. 'none' reservado para carreira evidentemente sem trilha de consultoria/finanças; firma não-julgável = null (desconhecido).",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Um valor (analyst/manager/partner) estabelece o tier mais alto atingido numa trilha de consultoria OU finanças/PE em firma cuja seriedade é estabelecida (marca conhecida, staffCountRange, calibre de cliente descrito, ou corroboração de pedigree pós-trilha) — boutiques inclusas.",
      doesNotImply:
        "O tier da trilha não estabelece por si só liderança de engajamento nem execução de transações reais — esses julgamentos ficam com consulting_engagement_leadership e has_executed_transactions, e pedigree acadêmico nunca carrega a leitura sozinho.",
      valueSemantics:
        "'none' só quando a carreira evidentemente não contém nenhuma trilha de consultoria/finanças (ex.: carreira pura de engenharia); firma cuja seriedade não pode ser estabelecida por nenhum critério vira null, nunca 'none' — a trilha existe, só a firma é incerta.",
      correlatedWith: [
        "consulting_engagement_leadership",
        "analytical_years",
        "has_executed_transactions",
        "ba_has_top_consulting",
        "ba_has_top_finance",
      ],
    },
  },
  analytical_years: {
    label: "Anos em trabalho analítico rigoroso",
    description:
      "Anos em que trabalho analítico rigoroso era a função primária (consultoria / estratégia / IB / PE / equity research / data science). NÃO restrito a marcas — dispara para boutiques. Em CONJUNÇÃO com tier alto de pós-graduação (programas apex selecionam pela qualidade do empregador), evidencia barra alta de operação em consultoria sem marca.",
    measurement: "llm",
    group: "ic-depth",
    scoring: {
      licenses:
        "Estabelece quantos anos cumulativos (intervalos não sobrepostos) a trajetória teve trabalho analítico rigoroso como ocupação primária — consultoria, strategy corporativo, IB, PE/VC, equity research, quant/data science, FP&A a fundo. É lido de título+empresa+tenure; descrição não é exigida quando a trilha em si define o trabalho.",
      doesNotImply:
        "O acúmulo de anos não estabelece que a pessoa foi dona de uma decisão ou de um artefato — para isso corrobore com has_deep_analytical_ownership ou has_technical_ic_artifact.",
      valueSemantics:
        "0 só quando a carreira evidentemente não tem nenhum ano analítico (todo cargo claramente não-analítico); null quando os cargos são genéricos ou esparsos demais para julgar — nunca leia um valor baixo como sinal de trabalho raso. Anos em boutique contam integralmente e estágios contam a valor de face.",
      correlatedWith: [
        "consulting_engagement_leadership",
        "top_consulting_track_level",
        "ba_years_in_finance",
        "ba_years_in_strategy",
      ],
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // Autonomy depth (2026-06-10 level-gradient fix). Graduated low/mid-band
  // autonomy evidence the floor-only palette lacked. Backed by
  // `llm-autonomy-depth.md` (+ deterministic num_ic_track_advancements in
  // seniority.ts). Gated via includeScopeSignals.
  // ───────────────────────────────────────────────────────────────────────
  autonomy_ceiling: {
    label: "Teto de autonomia demonstrada",
    description:
      "Maior grau de autodireção evidenciado: dirigido / autogerido / IC independente / totalmente autônomo. Sinal graduado — preenche a faixa L2–L3 que a paleta antiga só alcançava por abstenção. Null = não estabelecido (NA), nunca 'dirigido' por padrão.",
    measurement: "llm",
    group: "autonomy-depth",
    scoring: {
      licenses:
        "O MAIOR grau de autodireção que a carreira demonstra no dia a dia — de trabalho dirigido a totalmente autônomo. Sinal graduado que estabelece o grau de autonomia mesmo quando descrições de cargo faltam, via prior de estrutura licenciado (trilha de consultoria ≥3 anos com progressão dentro da trilha, ou prática solo sustentada multi-ano).",
      doesNotImply:
        "Um rung alto não estabelece escopo de impacto, porte de decisão ou senioridade de gestão — mede apenas o grau de supervisão sobre o COMO do trabalho, não o QUE foi decidido nem o resultado.",
      valueSemantics:
        "Enum ordinal directed < self_managing < independent_ic < fully_autonomous; null = não estabelecido — NUNCA 'directed' por padrão (ausência de descrição não é evidência de baixa autonomia); pedigree (tier de instituição, prestígio do empregador) só pode corroborar um prior de estrutura já existente, nunca sustentá-lo sozinho.",
      correlatedWith: ["has_independent_ownership", "has_self_directed_role"],
    },
  },
  has_independent_ownership: {
    label: "Ownership independente de workstream",
    description:
      "Foi dono de um workstream/processo/entregável recorrente ponta a ponta como IC, decidindo COMO sem ser instruído. Barra mais baixa que o ownership analítico profundo (não exige ter movido uma decisão de alto risco). Inferível da estrutura (trilha de consultoria ≥3 anos com progressão; único/primeiro numa função). null = desconhecido, nunca prova de ausência.",
    measurement: "llm",
    group: "autonomy-depth",
    scoring: {
      licenses:
        "A pessoa foi dona, como IC, de um workstream/processo/entregável NOMEADO ponta a ponta — escolheu o método e entregou com revisão apenas periódica. Barra deliberadamente mais baixa que um artefato que moveu uma decisão de alto risco.",
      doesNotImply:
        "Não estabelece que o trabalho moveu uma decisão de alto risco ou teve escopo organizacional amplo — apenas que houve ownership do método num entregável definido.",
      valueSemantics:
        "null = desconhecido, nunca prova de ausência; false só quando a estrutura mostra positivamente a ausência (todos os papéis descritos como suporte a entregável de terceiros, na carreira inteira); licenciável por estrutura (trilha de consultoria ≥3 anos com progressão sênior, ou títulos sole/first-in-function) mesmo com descrições vazias.",
      correlatedWith: ["autonomy_ceiling", "has_self_directed_role"],
    },
  },
  has_self_directed_role: {
    label: "Papel estruturalmente autodirigido",
    description:
      "Contexto de baixa supervisão por estrutura em que rodou trabalho recorrente sozinho: consultor/freelancer independente com atuação multi-ano, único/primeiro numa função, ponto de contato solo de cliente. Status 'freelancer' sozinho não basta. null = desconhecido, nunca prova de ausência.",
    measurement: "llm",
    group: "autonomy-depth",
    scoring: {
      licenses:
        "A pessoa atuou num contexto estruturalmente de baixa supervisão, rodando trabalho recorrente sozinha — consultoria/freelance independente multi-ano com clientes ou domínio nomeados, ou única/primeira pessoa numa função.",
      doesNotImply:
        "O rótulo isolado 'freelancer' ou 'self-employed' não estabelece nada por si só — só conta quando há multi-ano, cliente/domínio nomeado ou função sole/first-in-function.",
      valueSemantics:
        "null = desconhecido, nunca prova de ausência (inclui o rótulo 'freelancer' nu, sem clientes/domínio/duração — isso é null, não false); false só quando a estrutura mostra positivamente que a pessoa sempre foi membro de time gerido por terceiros.",
      correlatedWith: ["autonomy_ceiling", "has_independent_ownership"],
    },
  },
  num_ic_track_advancements: {
    label: "Avanços na trilha IC",
    description:
      "Subidas de título dentro do mesmo employer que permanecem na trilha IC (ex.: Analyst→Senior Analyst), sem cruzar gestão. Proxy de confiança ganha como IC.",
    measurement: "numeric",
    group: "progression",
    scoring: {
      licenses:
        "Cada ocorrência estabelece uma subida de rank de título dentro do mesmo empregador que permaneceu na trilha IC, sem cruzar para gestão — evidência de confiança crescente como contribuidor individual.",
      doesNotImply:
        "Não estabelece profundidade técnica ou de escopo — é apenas contagem de degraus de título; corrobore com sinais de profundidade técnica ou autonomia para saber se a responsabilidade de fato se aprofundou.",
      valueSemantics:
        "Conta só subidas adjacentes dentro de um mesmo empregador cujo rank de destino fica abaixo do piso de gestão; promoções que cruzam para gestão aparecem em outros sinais (ex.: num_loose_promotions), não neste.",
      correlatedWith: ["num_within_employer_function_switches"],
    },
  },

  // ───────────────────────────────────────────────────────────────────────
  // LA signal suite (2026-06-11). Extracted by the LA-owned extractors in
  // `server/labs/level-assessment/signals/` (Sonnet, three-valued contract:
  // null = desconhecido, nunca evidência de ausência). See
  // docs/labs/level-assessment/signal-suite.md.
  // ───────────────────────────────────────────────────────────────────────
  career_timeline: {
    label: "Linha do tempo de carreira tipada",
    description:
      "Estrutura completa da carreira, um segmento por cargo em ordem cronológica: período · empresa (função, trilha/senioridade, anos, tier da empresa quando reconhecida). Ex.: '2011–2014 · Acme Strategy (consulting, senior_ic, 3.2y, T2) → …'. É o substrato que um humano lê — tenure por tipo de função, progressão dentro da trilha e calibre dos empregadores num único sinal. Rico em contexto, não é um floor-setter: corrobora e situa os sinais fortes.",
    measurement: "llm",
    group: "progression",
  },

  // engineering depth (active — promoted 2026-06-25; server/labs/level-assessment/signals/la-engineering-depth.ts)
  primary_engineering_domain: {
    label: "Domínio de engenharia principal",
    description:
      "Que tipo de engenharia a carreira predominantemente é (backend, frontend, dados, ML/IA, infra/plataforma, …). Porta de encaixe na disciplina; null quando não é carreira de engenharia.",
    measurement: "llm",
    group: "engineering",
  },
  num_major_stack_transitions: {
    label: "Trocas significativas de stack",
    description:
      "Quantas vezes mudou de stack/domínio técnico de forma material ao longo da carreira (ex.: PHP→Go). Proxy de adaptabilidade.",
    measurement: "llm",
    group: "engineering",
  },
  has_recent_stack_adoption: {
    label: "Adoção recente de stack nova",
    description:
      "Assumiu uma stack/linguagem materialmente nova em cargo recente (~3 anos). Proxy de disposição a aprender o novo.",
    measurement: "llm",
    group: "engineering",
  },
  has_legacy_modernization_experience: {
    label: "Experiência com legado / modernização",
    description:
      "Investigou, migrou, refatorou ou resgatou sistema legado/herdado. Proxy biográfico para investigação em sistemas desconhecidos (exige evidência em texto para TRUE).",
    measurement: "llm",
    group: "engineering",
  },
  build_orientation: {
    label: "Orientação construir vs. manter",
    description:
      "Carreira pende para construir o novo (greenfield), manter/operar o existente (maintenance) ou misto.",
    measurement: "llm",
    group: "engineering",
  },
  system_scale_bucket: {
    label: "Escala do maior sistema (faixa)",
    description:
      "Escala do maior sistema em que plausivelmente atuou, por números declarados ou pela escala conhecida do empregador.",
    measurement: "llm",
    group: "engineering",
  },
  domain_complexity: {
    label: "Complexidade do domínio",
    description:
      "Criticidade do domínio do problema, principalmente pela indústria do empregador: padrão, regulado (saúde/fintech) ou alto risco (safety-critical).",
    measurement: "llm",
    group: "engineering",
  },
  eng_track_orientation: {
    label: "Trilha: IC vs. gestão",
    description:
      "Formato da trilha de senioridade: aprofundamento como IC, gestão de pessoas, ou híbrido. NÃO é qualidade de influência — um IC profundo pode ter influência ampla.",
    measurement: "llm",
    group: "engineering",
  },
  has_production_ownership: {
    label: "Ownership de produção",
    description:
      "Operou sistemas em produção: on-call, SRE/DevOps/plataforma, plantão, responsabilidade por incidentes. Proxy para entrega e operação responsáveis.",
    measurement: "llm",
    group: "engineering",
  },
  oss_contribution_depth: {
    label: "Profundidade em open-source",
    description:
      "Pegada em open-source quando o perfil a mostra: mantenedor(a) ou contribuidor(a). Sem 'none' — ausência de OSS não é observável; null quando não mostrado.",
    measurement: "llm",
    group: "engineering",
  },
};

/** De-case an internal token into a readable fallback ("staff_archetype" →
 *  "Staff archetype") so a raw snake_case identifier never reaches the UI when a
 *  key/value is missing from the catalog. */
function humanizeToken(token: string): string {
  const words = token.replace(/[_-]+/g, " ").trim();
  return words.length === 0 ? "—" : words.charAt(0).toUpperCase() + words.slice(1);
}

/** Lookup with sensible fallback when a key isn't yet labeled. The fallback is
 *  de-cased (never the raw snake_case key) — see [[level-assessment-signal-labels]]. */
/** Compact one-line rendering of a signal's curated scoring semantics, for
 *  interpolation into generator meta-prompts and runtime glossaries. Returns
 *  "" for uncurated signals so call sites degrade to the plain description. */
export function renderScoringNotes(meta: SignalLabel): string {
  if (!meta.scoring) return "";
  const s = meta.scoring;
  const parts = [` O que estabelece: ${s.licenses}`, ` NÃO implica: ${s.doesNotImply}`];
  if (s.valueSemantics) parts.push(` Valores: ${s.valueSemantics}`);
  if (s.correlatedWith && s.correlatedWith.length > 0) {
    parts.push(
      ` Leitura correlacionada com: ${s.correlatedWith.join(", ")} (corrobora, nunca soma como evidência independente).`,
    );
  }
  return parts.join("");
}

export function labelForSignal(key: string): SignalLabel {
  return (
    SIGNAL_LABELS[key] ?? {
      label: humanizeToken(key),
      description: "(sem descrição registrada)",
      measurement: "numeric",
      group: "priors",
    }
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Signal lifecycle / availability ("manage, don't delete")
// ───────────────────────────────────────────────────────────────────────────

/**
 * Non-`active` signal statuses, declared centrally so the whole availability
 * state of the suite is reviewable in ONE place — one entry per non-active
 * signal; anything absent defaults to `active`. Grouped by the consolidation
 * cluster each demotion belongs to; every `deprecated` entry names its surviving
 * canonical signal so the matrix UI can explain the demotion to an author.
 *
 * Every key MUST exist in `SIGNAL_LABELS` (asserted by `signal-status.test.ts`).
 * Deprecating a signal NEVER stops its extractor or rewrites a persisted matrix —
 * it only removes the signal from new selection (bootstrap / matrix UI / GEPA),
 * so the change is safe on existing models and reversible by deleting the entry.
 *
 * NOTE: these are the high-confidence, mechanical-duplicate collapses (one
 * construct encoded twice, the canonical strictly dominates for scoring). The
 * more semantic collapses (prestige, tenure, scope, founder, title-rank) are
 * deliberately NOT deprecated here yet — they may carry distinct signal for the
 * existing FDE/comp-universal models, so they wait until a calibration run
 * confirms no regression. The whole point of this layer is to make that a
 * one-line, reversible flip.
 */
export const SIGNAL_STATUS: Record<string, SignalStatus> = {
  // ── Cluster: keyword↔LLM duplicates. The LLM read of the actual evidence
  //    supersedes the title-keyword proxy. The keyword extractor keeps running
  //    (it primes / falls back the LLM block) — it's just no longer routable on
  //    its own, where it would double-count the same construct on an axis.
  pm_years_in_management: "deprecated", // → pm_llm_years_in_management
  pm_num_management_titles: "deprecated", // → pm_llm_years_in_management / pm_llm_has_director_or_vp_title
  pm_has_executive_role: "deprecated", // → pm_llm_has_director_or_vp_title
  pm_has_program_or_pmo: "deprecated", // → pm_llm_has_multi_team_program_evidence
  sm_has_exec_liaison: "deprecated", // → sm_llm_has_executive_stakeholder_influence
  ps_years_in_product: "deprecated", // → ps_llm_product_or_systems_practice_years
  ps_years_in_engineering_or_design: "deprecated", // → ps_llm_product_or_systems_practice_years

  // ── Cluster: promotion velocity. LA prefers the LOOSE family — its classifier
  //    reads position descriptions (scope, reports, P&L), not just the title, so
  //    it catches description-based promotions the title-only STRICT family
  //    misses (see signal-suite.md). The strict block stays extracted because
  //    candidate-intelligence uses it; it's just no longer routable in LA.
  //    Survivors: the loose family + promotion_cadence_regularity.
  num_strict_promotions: "deprecated", // → num_loose_promotions
  avg_promo_interval_years: "deprecated", // → avg_loose_promo_interval_years
  num_employers_with_promotion: "deprecated", // → num_employers_with_loose_promotion
  has_double_promotion: "deprecated", // → has_double_loose_promotion

  // ── Cluster: dead-input. These depend on data the LinkedIn actor does not
  //    return (company staff-count ranges; ingested industry history), so they
  //    are ~always NA today. Withheld from routing until that ingestion exists —
  //    this is NOT redundancy; flip back to active when the data lands.
  earliest_employee_rank: "deprecated",
  earliest_employee_bucket: "deprecated",
  years_at_small_non_vc_company: "deprecated",
  years_in_primary_industry: "deprecated",
  industry_focus_ratio: "deprecated",

  // The engineering-depth block (la-engineering-depth.ts) is intentionally
  // `active` (promoted from `experimental` on 2026-06-25) — so it is NOT listed
  // here (absent ⇒ active). It is now bootstrap-offered, matrix-selectable, and
  // GEPA-eligible. Still biographical proxies, not craft: route MODERATE/WEAK,
  // never STRONG floors, and validate on the engineering calibration corpus.
};

/** Lifecycle status of a signal — `active` for anything not in SIGNAL_STATUS. */
export function getSignalStatus(key: string): SignalStatus {
  return SIGNAL_STATUS[key] ?? "active";
}

/** Offered to the bootstrap meta-prompt's candidate pool? Only `active` signals
 *  are auto-routed into a freshly bootstrapped model (experimental + deprecated
 *  are withheld). */
export function isBootstrapOffered(key: string): boolean {
  return getSignalStatus(key) === "active";
}

/** Selectable for NEW routing in the matrix UI and eligible for the GEPA search
 *  space? `active` and `experimental` qualify; `deprecated` does not. A
 *  `deprecated` signal already routed in a model still renders (flagged) and
 *  keeps scoring — this only gates fresh selection. */
export function isSelectableForRouting(key: string): boolean {
  return getSignalStatus(key) !== "deprecated";
}

/**
 * Human-readable Portuguese labels for the CATEGORICAL (string-valued) signals.
 * Their extractors emit machine tokens (e.g. `tech_lead`, `sticky`, `none`) that
 * must NEVER reach a recruiter — `humanizeSignalValue` translates them. Numeric
 * ranges ("21-100", "<50") are already readable and pass through untranslated.
 * Keep this in sync with the value unions in `server/labs/level-assessment/signals/**`.
 */
export const SIGNAL_VALUE_LABELS: Record<string, Record<string, string>> = {
  staff_archetype: {
    tech_lead: "Liderança técnica",
    architect: "Arquitetura de sistemas",
    solver: "Resolução de problemas complexos",
    right_hand: "Braço-direito de liderança",
  },
  autonomy_ceiling: {
    directed: "Trabalho dirigido",
    self_managing: "Autogestão",
    independent_ic: "Contribuidor individual independente",
    fully_autonomous: "Totalmente autônomo",
  },
  top_consulting_track_level: {
    none: "Sem trilha de consultoria/finanças",
    analyst: "Analista",
    manager: "Gerente",
    partner: "Sócio",
  },
  tenure_shape: {
    sticky: "Vínculos longos",
    mixed: "Vínculos mistos",
    rotating: "Vínculos curtos",
  },
  transaction_scale: {
    none: "Sem transação relevante",
    "<10M": "Até US$ 10 mi",
    "10-100M": "US$ 10–100 mi",
    "100M-1B": "US$ 100 mi – 1 bi",
    "1B+": "Acima de US$ 1 bi",
  },
  managed_team_size_bucket: {
    none: "Nenhum time direto",
    "1-5": "1–5 pessoas",
    "6-20": "6–20 pessoas",
    "21-100": "21–100 pessoas",
    "100+": "Mais de 100 pessoas",
  },
  peak_org_size_bucket: {
    "<50": "Menos de 50 pessoas",
    "50-200": "50–200 pessoas",
    "201-1000": "201–1.000 pessoas",
    "1000+": "Mais de 1.000 pessoas",
  },
  revenue_scaled_bucket: {
    none: "Sem receita atribuída",
    "<1M": "Até US$ 1 mi",
    "1-10M": "US$ 1–10 mi",
    "10-100M": "US$ 10–100 mi",
    "100M+": "Acima de US$ 100 mi",
  },
  capital_raised_bucket: {
    none: "Sem capital captado",
    "<1M": "Até US$ 1 mi",
    "1-10M": "US$ 1–10 mi",
    "10-50M": "US$ 10–50 mi",
    "50M+": "Acima de US$ 50 mi",
  },
  headcount_grown_bucket: {
    none: "Não construiu time",
    "to-10": "Até 10 pessoas",
    "to-50": "Até 50 pessoas",
    "50+": "Mais de 50 pessoas",
  },
  earliest_employee_bucket: {
    founder: "Fundador(a)",
    "1-20": "Entre os 20 primeiros",
    "21-50": "Entre os 50 primeiros",
    "51-100": "Entre os 100 primeiros",
    ">100": "Após os 100 primeiros",
  },
  primary_engineering_domain: {
    backend: "Backend",
    frontend: "Frontend",
    fullstack: "Full-stack",
    mobile: "Mobile",
    data: "Dados / Data",
    ml_ai: "ML / IA",
    infra_platform: "Infra / Plataforma",
    embedded: "Embarcados",
    security: "Segurança",
    other: "Outro",
  },
  build_orientation: {
    greenfield: "Construção do zero (greenfield)",
    mixed: "Misto",
    maintenance: "Manutenção / operação",
  },
  system_scale_bucket: {
    small: "Pequena escala",
    mid: "Escala média",
    high: "Alta escala",
    hyperscale: "Hiperescala (global)",
  },
  domain_complexity: {
    standard: "Domínio padrão",
    regulated: "Domínio regulado",
    high_stakes: "Domínio crítico",
  },
  eng_track_orientation: {
    ic_deepening: "Aprofundamento como IC",
    management: "Gestão de pessoas",
    hybrid: "Híbrido",
  },
  oss_contribution_depth: {
    contributor: "Contribuidor(a)",
    maintainer: "Mantenedor(a)",
  },
};

/**
 * @public — the display value for a signal in CLIENT-FACING surfaces (the
 * signals table and the decisive-signal cards). Categorical machine tokens are
 * translated via SIGNAL_VALUE_LABELS; booleans → Sim/Não; numbers are formatted
 * PT-BR; numeric ranges pass through; an uncatalogued token is de-cased so no
 * snake_case ever shows. `null` = the extractor abstained (unknown).
 */
export function humanizeSignalValue(key: string, value: number | boolean | string | null): string {
  if (value === null) return "Não observado";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number")
    return Number.isInteger(value) ? String(value) : value.toFixed(1).replace(".", ",");
  const mapped = SIGNAL_VALUE_LABELS[key]?.[value];
  if (mapped) return mapped;
  if (value === "none" || value.trim() === "") return "Nenhum";
  if (/\d/.test(value)) return value; // numeric range (e.g. "21-100", "<50") — already readable
  return humanizeToken(value);
}
